// VitaMD editorial pipeline dashboard.
//
// Hits /api/admin/pipeline (proxied to news-engine) every 15s and renders
// a 5-column Kanban. The browser handles HTTP Basic auth natively —
// first fetch returns 401 with WWW-Authenticate, browser prompts for
// password once, then remembers it for the session.
//
// Single interactive action: "Approve & Publish" on stage-4 cards.
import { useEffect, useState, useCallback } from 'react';
import { Loader2, CheckCircle2, AlertCircle, ExternalLink, RefreshCw, Sun, Moon, Plus, Search, Trash2, ChevronDown, ChevronUp, Package, Download, Upload, Radio, Zap, RotateCw, Pencil, MessageSquare, Save, X, Play } from 'lucide-react';

const POLL_MS = 15_000;

export default function Admin() {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [approving, setApproving] = useState({});
  // Run-scoped view: the dashboard reflects ONE run at a time. Blank until a
  // run is started (Collect Signals) or loaded from History. The news library
  // + published archive stay persistent (they aren't produced by one run).
  const [activeRun, setActiveRun] = useState(null);   // run summary currently loaded
  const [runData, setRunData] = useState(null);       // { run, signals, ranked, segments }
  const [runs, setRuns] = useState([]);               // durable history list
  const [historyOpen, setHistoryOpen] = useState(false);
  const [theme, setTheme] = useState(() => {
    if (typeof window === 'undefined') return 'dark';
    return localStorage.getItem('vitamd-admin-theme') || 'dark';
  });
  const isLight = theme === 'light';

  useEffect(() => {
    if (typeof window === 'undefined') return;
    localStorage.setItem('vitamd-admin-theme', theme);
  }, [theme]);

  // Tailwind class bundles for the two themes — now aligned with the
  // vita-* brand palette used by the marketing pages (vitamd.health/start).
  // Navy bg + vita-accent (light blue) instead of dark/amber.
  const t = isLight ? {
    page: 'bg-stone-50 text-stone-900',
    header: 'border-stone-200',
    headerTitleAccent: 'text-vita-accent',
    headerSubtle: 'text-stone-500',
    btn: 'border-stone-300 text-stone-600 hover:border-vita-accent hover:text-vita-accent',
    colNeutral: 'bg-white border-stone-200',
    colHighlight: 'bg-vita-accent/10 border-vita-accent/40',
    colHeader: 'border-stone-200',
    colTitle: 'text-stone-700',
    colTitleHighlight: 'text-vita-accent',
    colHint: 'text-stone-500',
    colCount: 'text-stone-400',
    cardBase: 'bg-white border-stone-200',
    cardAccent: 'bg-white border-vita-accent/50',
    cardFail: 'bg-red-50 border-red-200',
    primaryText: 'text-stone-900',
    secondaryText: 'text-stone-600',
    mutedText: 'text-stone-500',
    veryMutedText: 'text-stone-400',
    divider: 'border-stone-200',
    emptyText: 'text-stone-400'
  } : {
    page: 'bg-vita-navy text-stone-100',
    header: 'border-vita-deep',
    headerTitleAccent: 'text-vita-accent',
    headerSubtle: 'text-vita-muted',
    btn: 'border-vita-deep text-vita-muted hover:border-vita-accent hover:text-vita-accent',
    colNeutral: 'bg-vita-deep/70 border-vita-deep',
    colHighlight: 'bg-vita-accent/5 border-vita-accent/30',
    colHeader: 'border-vita-deep',
    colTitle: 'text-stone-300',
    colTitleHighlight: 'text-vita-accent',
    colHint: 'text-vita-muted',
    colCount: 'text-vita-muted',
    cardBase: 'bg-vita-deep border-vita-deep',
    cardAccent: 'bg-vita-deep border-vita-accent/40',
    cardFail: 'bg-red-950/40 border-red-900',
    primaryText: 'text-stone-200',
    secondaryText: 'text-stone-300',
    mutedText: 'text-vita-muted',
    veryMutedText: 'text-vita-muted/70',
    divider: 'border-vita-deep',
    emptyText: 'text-vita-muted/60'
  };

  const fetchPipeline = useCallback(async () => {
    try {
      const resp = await fetch('/api/admin/pipeline', { credentials: 'include' });
      if (resp.status === 401) {
        setError('Auth required. Reload and enter the admin password.');
        setLoading(false);
        return;
      }
      if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
      const json = await resp.json();
      setData(json);
      setError(null);
      setLastUpdated(new Date());
      setLoading(false);
    } catch (err) {
      setError(err.message);
      setLoading(false);
    }
  }, []);

  const fetchRuns = useCallback(async () => {
    try {
      const r = await fetch('/api/admin/runs', { credentials: 'include' });
      if (r.ok) { const j = await r.json(); setRuns(j.runs || []); }
    } catch { /* history is non-critical */ }
  }, []);

  // Load one run's scoped data into the dashboard (its signals, ranked
  // searches, and drafted segments). Called on run completion and on History
  // selection.
  const loadRun = useCallback(async (runId) => {
    if (!runId) return;
    try {
      const r = await fetch(`/api/admin/runs/${runId}/data`, { credentials: 'include' });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const j = await r.json();
      setRunData(j);
      setActiveRun(j.run);
      setHistoryOpen(false);
      fetchRuns();
    } catch (err) {
      alert(`Failed to load run: ${err.message}`);
    }
  }, [fetchRuns]);

  useEffect(() => {
    fetchPipeline();
    fetchRuns();
    const id = setInterval(() => { fetchPipeline(); fetchRuns(); }, POLL_MS);
    return () => clearInterval(id);
  }, [fetchPipeline, fetchRuns]);

  const regenSegment = async (segId, overridePersona) => {
    const label = overridePersona ? `as ${overridePersona}` : 'with current persona';
    if (!confirm(`Regenerate commentary for segment ${segId} ${label}? Abandons the existing draft and creates a fresh one using the current prompt version (script costs ~$0.02).`)) return;
    setApproving((p) => ({ ...p, [segId]: true }));
    try {
      const resp = await fetch(`/api/admin/segments/${segId}/regen`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(overridePersona ? { persona: overridePersona } : {})
      });
      if (!resp.ok) {
        const body = await resp.json().catch(() => ({}));
        throw new Error(body.error || `HTTP ${resp.status}`);
      }
      await fetchPipeline();
    } catch (err) {
      alert(`Regen failed: ${err.message}`);
    } finally {
      setApproving((p) => { const n = { ...p }; delete n[segId]; return n; });
    }
  };

  const buildBundle = async (segId) => {
    if (!confirm(`Build slideshow bundle for segment ${segId}? Generates ElevenLabs audio + ~6 AI/stock images + text cards (~$0.30-0.45). The download link appears here once it's ready (~1-2 min).`)) return;
    setApproving((p) => ({ ...p, [segId]: true }));
    try {
      const resp = await fetch(`/api/admin/segments/${segId}/build-bundle`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' }
      });
      if (!resp.ok) {
        const body = await resp.json().catch(() => ({}));
        throw new Error(body.error || `HTTP ${resp.status}`);
      }
      await fetchPipeline();
    } catch (err) {
      alert(`Bundle build failed: ${err.message}`);
    } finally {
      setApproving((p) => { const n = { ...p }; delete n[segId]; return n; });
    }
  };

  // ④ Review shows the active run's drafts that still need a human — exclude
  // already-published ones (those live in ⑤). Empty until a run is loaded.
  const runReviewSegments = (runData?.segments || []).filter((s) => s.status !== 'published');

  if (loading) {
    return (
      <div className={`min-h-screen flex items-center justify-center ${t.page}`}>
        <Loader2 className="w-6 h-6 animate-spin text-vita-accent" />
        <span className="ml-3">Loading pipeline…</span>
      </div>
    );
  }

  if (error && !data) {
    return (
      <div className={`min-h-screen flex items-center justify-center ${t.page}`}>
        <div className="max-w-md text-center">
          <AlertCircle className="w-8 h-8 text-red-500 mx-auto" />
          <p className={`mt-4 font-medium ${t.primaryText}`}>{error}</p>
          <p className={`mt-2 text-sm ${t.mutedText}`}>
            If you weren’t prompted for a password, refresh the page (Cmd+Shift+R) to clear cached Basic credentials.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className={`min-h-screen ${t.page}`}>
      <div className={`border-b ${t.header} px-6 py-4 flex items-center justify-between`}>
        <div>
          <h1 className="font-serif text-2xl">
            <span className={isLight ? 'text-stone-900' : 'text-stone-50'}>Vita</span>
            <span className={t.headerTitleAccent}>MD</span>
            <span className={`ml-4 text-base font-sans ${t.headerSubtle}`}>editorial pipeline</span>
          </h1>
        </div>
        <div className={`flex items-center gap-3 text-xs ${t.headerSubtle}`}>
          {activeRun ? (
            <span className="inline-flex items-center gap-1.5">
              <span className={t.headerTitleAccent}>● run loaded</span>
              <span title={activeRun.id}>{(activeRun.geos || []).map((g) => GEO_LABELS[g] || g).join(', ') || activeRun.kind} · {String(activeRun.started_at || '').slice(5, 16).replace('T', ' ')}</span>
              <button onClick={() => { setActiveRun(null); setRunData(null); }} className={`underline hover:${t.primaryText}`} title="clear — return to blank">clear</button>
            </span>
          ) : (
            <span className={t.emptyText}>no run loaded</span>
          )}
          <button
            onClick={() => { setHistoryOpen((v) => !v); fetchRuns(); }}
            className={`inline-flex items-center gap-1 px-3 py-1.5 rounded border transition ${t.btn}`}
          >
            <RotateCw className="w-3 h-3" /> History
          </button>
          <button
            onClick={() => setTheme(isLight ? 'dark' : 'light')}
            className={`inline-flex items-center gap-1 px-3 py-1.5 rounded border transition ${t.btn}`}
            title={isLight ? 'Switch to dark theme' : 'Switch to light theme'}
          >
            {isLight ? <Moon className="w-3 h-3" /> : <Sun className="w-3 h-3" />}
            {isLight ? 'dark' : 'light'}
          </button>
          <button onClick={fetchPipeline} className={`inline-flex items-center gap-1 px-3 py-1.5 rounded border transition ${t.btn}`}>
            <RefreshCw className="w-3 h-3" /> refresh
          </button>
        </div>
      </div>

      <BufferStatusBanner t={t} />

      {historyOpen && (
        <HistoryDrawer t={t} runs={runs} activeRunId={activeRun?.id} onSelect={loadRun} onClose={() => setHistoryOpen(false)} />
      )}

      <ControlPanel t={t} isLight={isLight} onRefresh={fetchPipeline} onRunComplete={loadRun} />

      <SignalsPanel t={t} run={activeRun} runData={runData} />

      <SourcesBanner t={t} />

      <DemandByState t={t} />

      <DemandByMetro t={t} />

      <TrendsMomentum t={t} />

      <AdDemandPanel t={t} />

      <AdDrafts t={t} />

      <div className="overflow-x-auto">
        <div className="flex gap-4 p-4 min-w-max">
          <Column t={t} title="News library · ingested" hint="Stories pulled from RSS — the pool Match draws from" count={data?.ingested?.length}>
            {(data?.ingested || []).map((r) => <IngestedCard key={r.id} row={r} t={t} />)}
          </Column>

          <Column t={t} title="News library · scored" hint="Editorial ranking 0–1" count={data?.scored?.length}>
            {(data?.scored || []).map((r) => <ScoredCard key={r.id} row={r} t={t} />)}
          </Column>

          <Column t={t} title="News library · auto-approved" hint="Weight-loss vertical, score ≥ 0.65" count={data?.approved?.length}>
            {(data?.approved || []).map((r) => <ApprovedCard key={r.id} row={r} t={t} />)}
          </Column>

          {/* Run-scoped: this run's drafts only. Blank until a run is loaded. */}
          <Column t={t} title="④ Review & approve" hint={activeRun ? 'This run’s drafted scripts → read → build bundle → approve' : 'Load a run to review its drafts'} count={runData ? runReviewSegments.length : undefined} highlight>
            {!activeRun && <div className={`text-xs ${t.emptyText} p-2`}>No run loaded. Run a pipeline above, or open History to load a previous run.</div>}
            {runReviewSegments.map((r) => (
              <PendingCard
                key={r.id}
                row={r}
                t={t}
                approving={approving[r.id]}
                onBuildBundle={() => buildBundle(r.id)}
                onRegen={(p) => regenSegment(r.id, p)}
              />
            ))}
          </Column>

          <Column t={t} title="⑤ Live" hint="Published to YouTube + /media" count={data?.published?.length}>
            {(data?.published || []).map((r) => <PublishedCard key={r.id} row={r} t={t} />)}
          </Column>
        </div>
      </div>

      {/* Out of the flow — config + monitors, collapsed by default. */}
      <SettingsMonitors t={t} isLight={isLight} />
    </div>
  );
}

function Column({ t, title, hint, count, highlight, children }) {
  return (
    <div className={`flex-none w-80 rounded-lg border ${highlight ? t.colHighlight : t.colNeutral}`}>
      <div className={`px-4 py-3 border-b ${t.colHeader}`}>
        <h2 className={`text-sm font-medium ${highlight ? t.colTitleHighlight : t.colTitle}`}>
          {title} <span className={`ml-1 ${t.colCount}`}>({count ?? 0})</span>
        </h2>
        <p className={`text-[11px] mt-0.5 ${t.colHint}`}>{hint}</p>
      </div>
      <div className="p-3 space-y-2 max-h-[calc(100vh-180px)] overflow-y-auto">
        {children?.length ? children : <p className={`text-xs italic px-1 py-4 ${t.emptyText}`}>empty</p>}
      </div>
    </div>
  );
}

function CardShell({ t, children, accent }) {
  return (
    <div className={`rounded border p-3 text-xs ${accent || t.cardBase}`}>
      {children}
    </div>
  );
}

// Compact freshness label for a library story. Prefers the story's publish
// date (how old the NEWS is — the editorial freshness signal); falls back to
// when we ingested it. Returns a relative string + a full-timestamp tooltip.
function storyAge(publishedAt, ingestedAt) {
  const iso = publishedAt || ingestedAt;
  if (!iso) return null;
  const d = new Date(iso);
  if (isNaN(d.getTime())) return null;
  const mins = Math.floor((Date.now() - d.getTime()) / 60000);
  const hrs = Math.floor(mins / 60);
  const days = Math.floor(hrs / 24);
  let rel;
  if (mins < 1) rel = 'just now';
  else if (mins < 60) rel = `${mins}m ago`;
  else if (hrs < 24) rel = `${hrs}h ago`;
  else if (days < 30) rel = `${days}d ago`;
  else rel = d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: '2-digit' });
  // Flag when we only know ingest time, not the story's publish date.
  return { rel: publishedAt ? rel : `ingested ${rel}`, title: d.toLocaleString() };
}

function IngestedCard({ row, t }) {
  const age = storyAge(row.published_at, row.ingested_at);
  return (
    <CardShell t={t}>
      <div className={`flex items-center justify-between ${t.mutedText}`}>
        <span className="uppercase tracking-wider">{row.source}</span>
        <span>#{row.id}</span>
      </div>
      <p className={`mt-1.5 leading-snug ${t.primaryText}`}>{row.title}</p>
      <div className="mt-2 flex items-center gap-2 text-[10px]">
        {row.vertical && <span className="uppercase tracking-wider text-vita-accent/80">{row.vertical}</span>}
        {age && <span className={t.veryMutedText} title={age.title}>· {age.rel}</span>}
        {row.status && row.status !== 'ingested' && (
          <>
            <span className={t.veryMutedText}>·</span>
            <span className={t.veryMutedText}>{row.status}</span>
          </>
        )}
      </div>
    </CardShell>
  );
}

function ScoredCard({ row, t }) {
  const age = storyAge(row.published_at, row.ingested_at);
  return (
    <CardShell t={t}>
      <div className="flex items-center justify-between">
        <span className={`${t.mutedText} uppercase tracking-wider`}>{row.source}</span>
        <span className="text-vita-accent font-mono">{Number(row.score).toFixed(2)}</span>
      </div>
      <p className={`mt-1.5 leading-snug ${t.primaryText}`}>{row.title}</p>
      <div className="mt-2 flex items-center gap-2 text-[10px]">
        {row.vertical && <span className={`uppercase tracking-wider ${t.mutedText}`}>{row.vertical}</span>}
        <span className={t.veryMutedText}>·</span>
        <span className={t.veryMutedText}>{row.status}</span>
        {age && <span className={t.veryMutedText} title={age.title}>· {age.rel}</span>}
      </div>
    </CardShell>
  );
}

function ApprovedCard({ row, t }) {
  const age = storyAge(row.published_at, row.ingested_at);
  return (
    <CardShell t={t}>
      <div className={`flex items-center justify-between ${t.mutedText}`}>
        <span className="uppercase tracking-wider">{row.source}</span>
        <span className="text-vita-accent font-mono">{Number(row.score).toFixed(2)}</span>
      </div>
      <p className={`mt-1.5 leading-snug ${t.primaryText}`}>{row.title}</p>
      <div className={`mt-2 text-[10px] ${t.veryMutedText}`}>
        approved by {row.reviewer} · {row.segment_count} segment{row.segment_count === 1 ? '' : 's'}
        {age && <span title={age.title}> · {age.rel}</span>}
      </div>
    </CardShell>
  );
}

function PendingCard({ row, t, approving, onBuildBundle, onRegen }) {
  const isFailed = row.status === 'failed';
  const hasBeats = Array.isArray(row.visual_beats) && row.visual_beats.length > 0;
  const bundleReady = row.bundle_ready_at && row.bundle_path;
  const bundleBuilding = !bundleReady && row.status_detail?.toLowerCase().includes('bundle');
  const isPublishing = row.status_detail?.toLowerCase().includes('publishing') || row.status_detail?.toLowerCase().includes('uploading');
  const [uploading, setUploading] = useState(false);
  const fileInputId = `mp4-upload-${row.id}`;

  // Edit / Refine modes.
  const [mode, setMode] = useState('view');                              // 'view' | 'edit' | 'refine'
  const [editScript, setEditScript] = useState(row.script || '');
  const [editOnScreen, setEditOnScreen] = useState(row.on_screen_title || '');
  const [editCta, setEditCta] = useState(row.cta || '');
  const [feedback, setFeedback] = useState('');
  const [busy, setBusy] = useState(false);
  const [editError, setEditError] = useState(null);

  // Reset locals when the row data flips (after refine/regen produces a new card).
  useEffect(() => {
    setMode('view');
    setEditScript(row.script || '');
    setEditOnScreen(row.on_screen_title || '');
    setEditCta(row.cta || '');
    setFeedback('');
    setEditError(null);
  }, [row.id, row.script, row.on_screen_title, row.cta]);

  const saveEdit = async () => {
    setBusy(true); setEditError(null);
    try {
      const resp = await fetch(`/api/admin/segments/${row.id}`, {
        method: 'PATCH',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ script: editScript, on_screen_title: editOnScreen, cta: editCta })
      });
      const body = await resp.json().catch(() => ({}));
      if (resp.status === 422) {
        setEditError(`QA blocked: ${(body.qa_issues || []).join('; ')}`);
        return;
      }
      if (!resp.ok) throw new Error(body.error || `HTTP ${resp.status}`);
      setMode('view');
    } catch (err) {
      setEditError(err.message);
    } finally {
      setBusy(false);
    }
  };

  const submitFeedback = async () => {
    if (!feedback.trim()) return;
    setBusy(true); setEditError(null);
    try {
      const resp = await fetch(`/api/admin/segments/${row.id}/refine`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ feedback })
      });
      const body = await resp.json().catch(() => ({}));
      if (!resp.ok) throw new Error(body.error || `HTTP ${resp.status}`);
      setMode('view');
      setFeedback('');
    } catch (err) {
      setEditError(err.message);
    } finally {
      setBusy(false);
    }
  };

  const onUpload = async (e) => {
    const file = e.target?.files?.[0];
    if (!file) return;
    if (!confirm(`Upload "${file.name}" (${(file.size/1024/1024).toFixed(1)} MB) as the final MP4 for segment #${row.id}? It will publish to YouTube (unlisted) immediately after upload completes.`)) {
      e.target.value = '';
      return;
    }
    setUploading(true);
    try {
      const form = new FormData();
      form.append('mp4', file);
      const resp = await fetch(`/api/admin/segments/${row.id}/upload-final`, {
        method: 'POST',
        credentials: 'include',
        body: form
      });
      if (!resp.ok) {
        const body = await resp.json().catch(() => ({}));
        throw new Error(body.error || `HTTP ${resp.status}`);
      }
    } catch (err) {
      alert(`Upload failed: ${err.message}`);
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  };

  return (
    <CardShell t={t} accent={isFailed ? t.cardFail : t.cardAccent}>
      <div className={`flex items-center justify-between ${t.mutedText}`}>
        <span className="uppercase tracking-wider">{row.source}</span>
        <span>seg #{row.id}</span>
      </div>
      <p className={`mt-1.5 leading-snug font-medium ${t.primaryText}`}>{row.news_item_title}</p>

      {row.matched_keyword && (
        <div className={`mt-2 inline-flex items-center gap-1.5 text-[10px] rounded px-2 py-1 border border-vita-accent/40 bg-vita-accent/5 text-vita-accent`}>
          <Zap className="w-3 h-3" />
          <span>from signal: <span className="font-medium">{row.matched_keyword}</span></span>
          {row.match_score != null && <span className={t.veryMutedText}>· {Number(row.match_score).toFixed(2)}</span>}
        </div>
      )}

      {/* Script / on-screen / CTA — view, edit, or refine mode */}
      {mode === 'edit' ? (
        <div className={`mt-3 pt-3 border-t ${t.divider} space-y-2`}>
          <div>
            <div className={`text-[10px] uppercase tracking-wider mb-1 ${t.mutedText}`}>on-screen</div>
            <input
              type="text"
              value={editOnScreen}
              onChange={(e) => setEditOnScreen(e.target.value)}
              maxLength={90}
              className={`w-full px-2 py-1.5 rounded border text-xs ${isLightBundle(t) ? 'bg-white border-stone-300 text-stone-900' : 'bg-stone-900 border-stone-800 text-stone-100'}`}
            />
          </div>
          <div>
            <div className={`text-[10px] uppercase tracking-wider mb-1 ${t.mutedText}`}>script ({row.duration_seconds}s · {row.persona})</div>
            <textarea
              value={editScript}
              onChange={(e) => setEditScript(e.target.value)}
              rows={10}
              className={`w-full px-2 py-1.5 rounded border text-xs leading-relaxed ${isLightBundle(t) ? 'bg-white border-stone-300 text-stone-900' : 'bg-stone-900 border-stone-800 text-stone-100'}`}
            />
          </div>
          <div>
            <div className={`text-[10px] uppercase tracking-wider mb-1 ${t.mutedText}`}>cta (≤40 chars)</div>
            <input
              type="text"
              value={editCta}
              onChange={(e) => setEditCta(e.target.value)}
              maxLength={40}
              className={`w-full px-2 py-1.5 rounded border text-xs ${isLightBundle(t) ? 'bg-white border-stone-300 text-stone-900' : 'bg-stone-900 border-stone-800 text-stone-100'}`}
            />
          </div>
          {editError && <div className="text-[10px] text-red-500">{editError}</div>}
          <div className="flex items-center gap-2">
            <button onClick={saveEdit} disabled={busy} className="inline-flex items-center gap-1 px-3 py-1.5 rounded bg-vita-accent text-vita-navy text-xs font-medium hover:bg-vita-bright disabled:opacity-50 transition">
              {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <Save className="w-3 h-3" />}
              Save
            </button>
            <button onClick={() => { setMode('view'); setEditError(null); }} className={`inline-flex items-center gap-1 px-3 py-1.5 rounded border text-xs ${t.btn}`}>
              <X className="w-3 h-3" /> Cancel
            </button>
          </div>
        </div>
      ) : mode === 'refine' ? (
        <div className={`mt-3 pt-3 border-t ${t.divider} space-y-2`}>
          <div className={`text-[10px] uppercase tracking-wider ${t.mutedText}`}>refine with notes</div>
          <p className={`text-[10px] leading-snug ${t.secondaryText}`}>
            Tell the AI what to change. The current script + your notes go to the model.
            Examples: <em>"use a fridge analogy instead"</em> · <em>"warmer intro, mention 'you' in the first line"</em> · <em>"end with a question"</em>.
          </p>
          <textarea
            value={feedback}
            onChange={(e) => setFeedback(e.target.value)}
            rows={5}
            placeholder="Operator notes…"
            className={`w-full px-2 py-1.5 rounded border text-xs leading-relaxed ${isLightBundle(t) ? 'bg-white border-stone-300 text-stone-900 placeholder-stone-400' : 'bg-stone-900 border-stone-800 text-stone-100 placeholder-stone-600'}`}
          />
          {editError && <div className="text-[10px] text-red-500">{editError}</div>}
          <div className="flex items-center gap-2">
            <button onClick={submitFeedback} disabled={busy || !feedback.trim()} className="inline-flex items-center gap-1 px-3 py-1.5 rounded bg-vita-accent text-vita-navy text-xs font-medium hover:bg-vita-bright disabled:opacity-50 transition">
              {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <Zap className="w-3 h-3" />}
              Refine
            </button>
            <button onClick={() => { setMode('view'); setEditError(null); }} className={`inline-flex items-center gap-1 px-3 py-1.5 rounded border text-xs ${t.btn}`}>
              <X className="w-3 h-3" /> Cancel
            </button>
          </div>
        </div>
      ) : (
        <>
          <div className={`mt-3 pt-3 border-t ${t.divider}`}>
            <div className={`text-[10px] uppercase tracking-wider mb-1 ${t.mutedText}`}>on-screen</div>
            <p className={`leading-snug ${t.secondaryText}`}>{row.on_screen_title}</p>
          </div>
          <div className="mt-3">
            <div className={`text-[10px] uppercase tracking-wider mb-1 ${t.mutedText}`}>script ({row.duration_seconds}s · {row.persona})</div>
            <p className={`leading-relaxed whitespace-pre-wrap ${t.secondaryText}`}>{row.script}</p>
          </div>
          <div className="mt-3">
            <div className={`text-[10px] uppercase tracking-wider mb-1 ${t.mutedText}`}>cta</div>
            <p className={`italic ${t.secondaryText}`}>{row.cta}</p>
          </div>
        </>
      )}

      {/* Visual storyboard summary */}
      {hasBeats && (
        <div className={`mt-3 pt-3 border-t ${t.divider}`}>
          <div className={`text-[10px] uppercase tracking-wider mb-1 ${t.mutedText}`}>storyboard ({row.visual_beats.length} beats)</div>
          <ol className={`text-[10px] space-y-0.5 ${t.secondaryText}`}>
            {row.visual_beats.map((b, i) => (
              <li key={i}>
                <span className={`uppercase tracking-wider mr-1 ${t.veryMutedText}`}>{String(i+1).padStart(2,'0')}</span>
                <span className="text-vita-accent/80">[{b.type}]</span>{' '}
                {b.content?.slice(0, 60)}{b.content?.length > 60 ? '…' : ''}
                <span className={`ml-1 ${t.veryMutedText}`}>· {b.suggested_seconds}s</span>
              </li>
            ))}
          </ol>
        </div>
      )}

      {!hasBeats && (
        <div className={`mt-3 pt-3 border-t ${t.divider} text-[10px] text-orange-500`}>
          ⚠️ No visual_beats — regenerate commentary with the new prompt before building bundle.
        </div>
      )}

      {/* Edit / Refine / Regen row: only visible in view mode + not in flight */}
      {mode === 'view' && !isPublishing && !bundleBuilding && (
        <div className={`mt-3 pt-3 border-t ${t.divider} space-y-2`}>
          <div className="flex items-center gap-2 flex-wrap">
            <button
              onClick={() => setMode('edit')}
              disabled={approving}
              title="Manually edit script / on-screen / CTA"
              className={`inline-flex items-center gap-1 px-2 py-1 rounded text-[10px] border ${t.btn} disabled:opacity-50`}
            >
              <Pencil className="w-3 h-3" /> Edit
            </button>
            <button
              onClick={() => setMode('refine')}
              disabled={approving}
              title="Give the AI notes and have it rewrite"
              className={`inline-flex items-center gap-1 px-2 py-1 rounded text-[10px] border ${t.btn} disabled:opacity-50`}
            >
              <MessageSquare className="w-3 h-3" /> Refine with notes
            </button>
            <button
              onClick={() => onRegen?.()}
              disabled={approving}
              title="Re-roll the script with the current prompt — same persona"
              className={`inline-flex items-center gap-1 px-2 py-1 rounded text-[10px] border ${t.btn} disabled:opacity-50`}
            >
              <RotateCw className="w-3 h-3" /> Regen
            </button>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <span className={`text-[10px] ${t.veryMutedText}`}>regen as:</span>
            {['executive', 'professional', 'physician'].filter((p) => p !== row.persona).map((p) => (
              <button
                key={p}
                onClick={() => onRegen?.(p)}
                disabled={approving}
                title={`Re-roll as ${p}`}
                className={`text-[10px] px-1.5 py-0.5 rounded border ${t.btn} disabled:opacity-50`}
              >
                {p === 'executive' ? 'Douglas' : p === 'professional' ? 'Jasper' : 'Michael'}
              </button>
            ))}
          </div>
        </div>
      )}

      <div className={`mt-4 pt-3 border-t ${t.divider}`}>
        {isFailed ? (
          <div className="text-red-500 text-xs">
            <AlertCircle className="w-3 h-3 inline mr-1" /> failed
            {row.status_detail && <div className="mt-1 text-[10px] text-red-400/80">{row.status_detail}</div>}
          </div>
        ) : isPublishing ? (
          <div className="flex items-center gap-2 text-vita-accent text-xs">
            <Loader2 className="w-3 h-3 animate-spin" />
            <span>{row.status_detail || 'publishing…'}</span>
          </div>
        ) : bundleReady ? (
          <div className="space-y-2">
            <a
              href={`/api/bundle/${row.id}/download`}
              className="w-full inline-flex items-center justify-center gap-2 rounded bg-vita-accent px-3 py-2 text-vita-navy font-medium hover:bg-vita-bright transition no-underline"
            >
              <Download className="w-4 h-4" />
              Download Bundle (.zip)
            </a>
            <input
              type="file"
              id={fileInputId}
              accept="video/mp4,video/quicktime,.mp4,.mov"
              onChange={onUpload}
              disabled={uploading}
              className="sr-only"
            />
            <label
              htmlFor={fileInputId}
              className={`w-full inline-flex items-center justify-center gap-2 rounded border border-vita-accent px-3 py-2 text-vita-accent font-medium hover:bg-vita-accent hover:text-vita-navy transition cursor-pointer ${uploading ? 'opacity-50 cursor-wait' : ''}`}
            >
              {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
              {uploading ? 'Uploading…' : 'Upload Final MP4 → Publish'}
            </label>
          </div>
        ) : bundleBuilding ? (
          <div className="flex items-center gap-2 text-vita-accent text-xs">
            <Loader2 className="w-3 h-3 animate-spin" />
            <span>Building bundle…</span>
            {row.status_detail && <span className={`text-[10px] ${t.mutedText}`}>{row.status_detail}</span>}
          </div>
        ) : (
          <button
            onClick={onBuildBundle}
            disabled={approving || !hasBeats}
            title={!hasBeats ? 'Regenerate commentary first — needs visual_beats' : 'Build the slideshow asset ZIP'}
            className="w-full inline-flex items-center justify-center gap-2 rounded bg-vita-accent px-3 py-2 text-vita-navy font-medium hover:bg-vita-bright disabled:opacity-50 disabled:cursor-not-allowed transition"
          >
            {approving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Package className="w-4 h-4" />}
            Generate Bundle
          </button>
        )}
      </div>
    </CardShell>
  );
}

function PublishedCard({ row, t }) {
  const watchUrl = row.youtube_video_id ? `https://youtu.be/${row.youtube_video_id}` : null;
  return (
    <CardShell t={t}>
      <div className={`flex items-center justify-between ${t.mutedText}`}>
        <span className="uppercase tracking-wider">{row.source}</span>
        <span>seg #{row.id}</span>
      </div>
      <p className={`mt-1.5 leading-snug ${t.primaryText}`}>{row.on_screen_title || row.news_item_title}</p>
      <div className={`mt-2 text-[10px] ${t.veryMutedText}`}>
        {row.persona} · {row.published_at && new Date(row.published_at).toLocaleString()}
      </div>
      {watchUrl && (
        <a
          href={watchUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="mt-2 inline-flex items-center gap-1 text-vita-accent hover:text-vita-bright"
        >
          watch <ExternalLink className="w-3 h-3" />
        </a>
      )}
    </CardShell>
  );
}

// ─── ControlPanel ───────────────────────────────────────────────────
// The START of the pipeline — and the only trigger that begins where the
// real process begins: discovering who's searching for what online. Everything
// downstream (rank → match → draft → approve → publish) flows from here. The
// manual button is a stand-in for the cron that will eventually run this on a
// daily / few-hour schedule. News-library refresh lives inside it as a
// secondary action (it's an input to matching, not a rival starting point).
function ControlPanel({ t, isLight, onRefresh, onRunComplete }) {
  const [expanded, setExpanded] = useState(true);
  return (
    <div className={`border-b ${t.header}`}>
      <button
        onClick={() => setExpanded(!expanded)}
        className={`w-full px-6 py-2.5 flex items-center justify-between text-xs uppercase tracking-wider ${t.mutedText} hover:${t.primaryText} transition`}
      >
        <span>Start here · discover trends</span>
        {expanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
      </button>
      {expanded && (
        <div className={`px-6 pb-4 grid grid-cols-1 md:grid-cols-2 gap-4`}>
          <CollectSignalsPanel t={t} isLight={isLight} onRefresh={onRefresh} onRunComplete={onRunComplete} />
        </div>
      )}
    </div>
  );
}

// Out-of-flow drawer: configuration (RSS sources) + monitors (API cost,
// access activity). Collapsed by default — these aren't part of the
// trend→approval story, they're plumbing you check occasionally.
function SettingsMonitors({ t, isLight }) {
  const [expanded, setExpanded] = useState(false);
  return (
    <div className={`border-t ${t.header} mt-4`}>
      <button
        onClick={() => setExpanded(!expanded)}
        className={`w-full px-6 py-2.5 flex items-center justify-between text-xs uppercase tracking-wider ${t.mutedText} hover:${t.primaryText} transition`}
      >
        <span>⚙ Settings &amp; monitors</span>
        {expanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
      </button>
      {expanded && (
        <div className={`px-6 pb-6 grid grid-cols-1 lg:grid-cols-3 gap-4`}>
          <SourcesPanel t={t} isLight={isLight} />
          <CostPanel t={t} isLight={isLight} />
          <ActivityPanel t={t} isLight={isLight} />
        </div>
      )}
    </div>
  );
}

function PanelCard({ t, title, children }) {
  return (
    <div className={`rounded-lg border p-4 ${t.colNeutral}`}>
      <h3 className={`text-xs uppercase tracking-wider mb-3 ${t.colTitle}`}>{title}</h3>
      <div className="space-y-2">{children}</div>
    </div>
  );
}

function TextField({ t, label, value, onChange, placeholder, type = 'text' }) {
  return (
    <label className="block">
      <span className={`text-[10px] uppercase tracking-wider ${t.mutedText}`}>{label}</span>
      <input
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className={`mt-1 w-full px-2 py-1.5 rounded border text-xs ${isLightBundle(t) ? 'bg-white border-stone-300 text-stone-900 placeholder-stone-400' : 'bg-stone-900 border-stone-800 text-stone-100 placeholder-stone-600'}`}
      />
    </label>
  );
}

function SelectField({ t, label, value, onChange, options }) {
  return (
    <label className="block">
      <span className={`text-[10px] uppercase tracking-wider ${t.mutedText}`}>{label}</span>
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className={`mt-1 w-full px-2 py-1.5 rounded border text-xs ${isLightBundle(t) ? 'bg-white border-stone-300 text-stone-900' : 'bg-stone-900 border-stone-800 text-stone-100'}`}
      >
        {options.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
      </select>
    </label>
  );
}

// Tiny helper: detect if we're rendering against the light bundle.
// Couldn't pass isLight into every leaf without a context, so I check
// one stable class from the bundle.
function isLightBundle(t) { return t?.page?.includes('bg-stone-50'); }

function SourcesPanel({ t, isLight }) {
  const [sources, setSources] = useState([]);
  const [url, setUrl] = useState('');
  const [label, setLabel] = useState('');
  const [slug, setSlug] = useState('');
  const [vh, setVh] = useState('');
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    try {
      const r = await fetch('/api/admin/sources', { credentials: 'include' });
      if (!r.ok) return;
      const j = await r.json();
      setSources(j.sources || []);
    } catch {}
  }, []);

  useEffect(() => { load(); }, [load]);

  const add = async () => {
    setError(null);
    setLoading(true);
    try {
      const r = await fetch('/api/admin/sources', {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ url, label, sourceSlug: slug, verticalHint: vh || null })
      });
      const j = await r.json();
      if (!r.ok) throw new Error(j.error || `HTTP ${r.status}`);
      setUrl(''); setLabel(''); setSlug(''); setVh('');
      await load();
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const remove = async (id) => {
    if (!confirm(`Delete source ${id}?`)) return;
    await fetch(`/api/admin/sources/${id}`, { method: 'DELETE', credentials: 'include' });
    await load();
  };

  return (
    <PanelCard t={t} title="Custom RSS Sources">
      <TextField t={t} label="Feed URL" value={url} onChange={setUrl} placeholder="https://…/rss.xml" />
      <div className="grid grid-cols-2 gap-2">
        <TextField t={t} label="Label" value={label} onChange={setLabel} placeholder="JAMA" />
        <TextField t={t} label="Slug" value={slug} onChange={setSlug} placeholder="jama" />
      </div>
      <SelectField t={t} label="Vertical hint (optional)" value={vh} onChange={setVh} options={[
        { value: '', label: '(let classifier decide)' },
        { value: 'weight-loss', label: 'weight-loss' },
        { value: 'trt', label: 'trt' },
        { value: 'ed', label: 'ed' },
        { value: 'general', label: 'general' }
      ]} />
      <button
        onClick={add}
        disabled={loading || !url || !label || !slug}
        className="w-full mt-2 inline-flex items-center justify-center gap-2 rounded border border-vita-accent px-3 py-2 text-vita-accent text-xs font-medium hover:bg-vita-accent hover:text-vita-navy disabled:opacity-50 transition"
      >
        {loading ? <Loader2 className="w-3 h-3 animate-spin" /> : <Plus className="w-3 h-3" />}
        Add Source
      </button>
      {error && <div className="text-[10px] text-red-500">{error}</div>}
      {sources.length > 0 && (
        <div className={`mt-2 pt-2 border-t ${t.divider} space-y-1`}>
          {sources.map((s) => (
            <div key={s.id} className={`flex items-center justify-between text-[10px] ${t.secondaryText}`}>
              <div className="truncate flex-1">
                <span className="font-medium">{s.label}</span>
                <span className={`ml-1 ${t.mutedText}`}>· {s.source_slug}</span>
                {s.last_error && <span className="ml-1 text-red-500" title={s.last_error}>· err</span>}
              </div>
              <button onClick={() => remove(s.id)} className={`ml-2 ${t.mutedText} hover:text-red-500`}>
                <Trash2 className="w-3 h-3" />
              </button>
            </div>
          ))}
        </div>
      )}
    </PanelCard>
  );
}

// ─── ActivityPanel ─────────────────────────────────────────────────
// Forensic view backing the runaway debugging story: every admin-route
// call is logged server-side and surfaced here. Polls /api/admin/activity
// every 20s. Highlights any (ip, path) pair that fired > 6 calls in any
// 60s window over the last hour — that's the pattern the 748-segment
// loop would have lit up if this view had existed.
function ActivityPanel({ t, isLight }) {
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  const [includePolling, setIncludePolling] = useState(false);
  const [expanded, setExpanded] = useState(false);

  const load = useCallback(async () => {
    try {
      const r = await fetch(`/api/admin/activity?limit=80&includePolling=${includePolling ? '1' : '0'}`, { credentials: 'include' });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      setData(await r.json());
      setErr(null);
    } catch (e) { setErr(e.message); }
  }, [includePolling]);

  useEffect(() => {
    load();
    const id = setInterval(load, 20_000);
    return () => clearInterval(id);
  }, [load]);

  const recent = data?.recent || [];
  const rates  = data?.rates  || [];
  const suspicious = data?.suspicious || [];

  // Lightweight color hint per status.
  const statusColor = (s) =>
    s >= 500 ? 'text-red-500'
    : s === 429 ? 'text-orange-500'
    : s >= 400 ? 'text-yellow-500'
    : 'text-emerald-500';

  // Short, identifiable UA — first parenthesized chunk or first 30 chars.
  const shortUa = (ua) => {
    if (!ua) return '—';
    const m = ua.match(/\(([^)]+)\)/);
    if (m) return m[1].slice(0, 36);
    return ua.slice(0, 36);
  };

  return (
    <div className="px-4 pt-2 pb-1">
      <div className={`rounded border ${t.colNeutral} p-3`}>
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-2">
            <Radio className={`w-3 h-3 ${suspicious.length ? 'text-red-500' : t.mutedText}`} />
            <div className={`text-[11px] uppercase tracking-wider ${t.primaryText}`}>Activity</div>
            <div className={`text-[10px] ${t.veryMutedText}`}>
              {recent.length} recent · {rates.length} endpoints/hr
              {suspicious.length > 0 && <span className="text-red-500 font-semibold"> · {suspicious.length} suspicious burst{suspicious.length > 1 ? 's' : ''}</span>}
            </div>
          </div>
          <div className="flex items-center gap-3">
            <label className={`flex items-center gap-1 text-[10px] ${t.mutedText} cursor-pointer`}>
              <input type="checkbox" checked={includePolling} onChange={(e) => setIncludePolling(e.target.checked)} />
              include pipeline polling
            </label>
            <button onClick={() => setExpanded(!expanded)} className={`text-[10px] ${t.btn} px-2 py-1 rounded border`}>
              {expanded ? <ChevronUp className="w-3 h-3 inline" /> : <ChevronDown className="w-3 h-3 inline" />} {expanded ? 'collapse' : 'expand'}
            </button>
            <button onClick={load} className={`text-[10px] ${t.btn} px-2 py-1 rounded border`}>refresh</button>
          </div>
        </div>

        {err && <div className="text-[10px] text-red-500">{err}</div>}

        {suspicious.length > 0 && (
          <div className={`mb-2 p-2 rounded border border-red-500/40 bg-red-500/5`}>
            <div className="text-[10px] text-red-500 font-semibold mb-1">⚠️ Suspicious bursts (&gt; 6 calls in any 60s window, last hour)</div>
            {suspicious.map((s, i) => (
              <div key={i} className={`text-[10px] ${t.secondaryText} font-mono`}>
                {s.peak_calls_in_60s}/60s peak · {s.total_calls} total · {s.source_ip} → {s.path}
              </div>
            ))}
          </div>
        )}

        {expanded && (
          <>
            <div className="grid grid-cols-2 gap-3">
              {/* RECENT HITS */}
              <div>
                <div className={`text-[10px] uppercase tracking-wider mb-1 ${t.mutedText}`}>Recent requests</div>
                <div className="max-h-72 overflow-y-auto space-y-0.5">
                  {recent.map((r) => (
                    <div key={r.id} className={`flex items-baseline gap-2 text-[10px] font-mono ${t.secondaryText}`} title={r.user_agent || ''}>
                      <span className={t.veryMutedText}>{(r.ts || '').slice(11, 19)}</span>
                      <span className={`font-semibold ${statusColor(r.status)}`}>{r.status}</span>
                      <span className={t.mutedText}>{r.method}</span>
                      <span className="flex-1 truncate">{r.path}</span>
                      <span className={t.veryMutedText}>{r.response_ms}ms</span>
                      <span className={t.veryMutedText}>{shortUa(r.user_agent)}</span>
                      {r.created_segment_id && <span className="text-vita-accent">#{r.created_segment_id}</span>}
                    </div>
                  ))}
                </div>
              </div>

              {/* PER-ENDPOINT RATES */}
              <div>
                <div className={`text-[10px] uppercase tracking-wider mb-1 ${t.mutedText}`}>Endpoint rate (last hour)</div>
                <div className="max-h-72 overflow-y-auto space-y-0.5">
                  {rates.map((r, i) => (
                    <div key={i} className={`flex items-baseline gap-2 text-[10px] font-mono ${t.secondaryText}`}>
                      <span className={`text-right inline-block w-10 ${r.calls_1h > 60 ? 'text-orange-500 font-semibold' : t.primaryText}`}>{r.calls_1h}</span>
                      <span className={t.mutedText}>{r.method}</span>
                      <span className="flex-1 truncate">{r.path}</span>
                      <span className={t.veryMutedText}>{r.distinct_ips} ip{r.distinct_ips > 1 ? 's' : ''}</span>
                      <span className={t.veryMutedText}>{r.avg_response_ms}ms avg</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

// ─── CostPanel ─────────────────────────────────────────────────────
// Real-time $-spend telemetry. Polls /api/admin/costs every 30s.
// Headline today's $ + 7-day chart + recent-call table. The fix that
// answers "what's burning credits" without waiting for a statement.
function CostPanel({ t, isLight }) {
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  const [expanded, setExpanded] = useState(false);

  const load = useCallback(async () => {
    try {
      const r = await fetch('/api/admin/costs', { credentials: 'include' });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      setData(await r.json());
      setErr(null);
    } catch (e) { setErr(e.message); }
  }, []);

  useEffect(() => {
    load();
    const id = setInterval(load, 30_000);
    return () => clearInterval(id);
  }, [load]);

  const today = data?.today || { total_usd: 0, call_count: 0, by_provider: [] };
  const last7 = data?.last_7d || [];
  const recent = data?.recent || [];
  const hourly = data?.hourly_24h || [];

  // Headline color shifts to red/orange when spend looks unusually high.
  const todayUsd = Number(today.total_usd || 0);
  const headlineColor =
    todayUsd > 50 ? 'text-red-500'
    : todayUsd > 20 ? 'text-orange-500'
    : todayUsd > 5  ? 'text-vita-accent'
    : t.primaryText;

  // Tiny sparkline of last 24h (newest right). Simple SVG, no library.
  const maxHourly = Math.max(0.0001, ...hourly.map((h) => Number(h.total_usd || 0)));
  const sparkBars = hourly.slice().reverse().map((h, i) => {
    const v = Number(h.total_usd || 0);
    const heightPct = (v / maxHourly) * 100;
    return (
      <div key={i} className="flex-1 flex items-end" title={`${h.hour?.slice(11, 16) || ''} · $${v.toFixed(3)}`}>
        <div
          className={`w-full bg-vita-accent/60 rounded-sm`}
          style={{ height: `${Math.max(heightPct, 4)}%`, minHeight: '2px' }}
        />
      </div>
    );
  });

  return (
    <div className="px-4 pt-2 pb-1">
      <div className={`rounded border ${t.colNeutral} p-3`}>
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-3">
            <Package className={`w-3 h-3 ${t.mutedText}`} />
            <div className={`text-[11px] uppercase tracking-wider ${t.primaryText}`}>Spend</div>
            <div className="flex items-baseline gap-2">
              <span className={`text-2xl font-semibold ${headlineColor}`}>${todayUsd.toFixed(2)}</span>
              <span className={`text-[10px] ${t.mutedText}`}>today · {today.call_count} LLM call{today.call_count === 1 ? '' : 's'}</span>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={() => setExpanded(!expanded)} className={`text-[10px] ${t.btn} px-2 py-1 rounded border`}>
              {expanded ? <ChevronUp className="w-3 h-3 inline" /> : <ChevronDown className="w-3 h-3 inline" />} {expanded ? 'collapse' : 'expand'}
            </button>
            <button onClick={load} className={`text-[10px] ${t.btn} px-2 py-1 rounded border`}>refresh</button>
          </div>
        </div>

        {err && <div className="text-[10px] text-red-500">{err}</div>}

        {/* Sparkline always visible */}
        <div className="flex items-end gap-0.5 h-8 mb-1">{sparkBars}</div>
        <div className={`text-[9px] ${t.veryMutedText} flex justify-between`}>
          <span>−24h</span>
          <span>last 24 hours · hourly $</span>
          <span>now</span>
        </div>

        {expanded && (
          <div className="mt-3 grid grid-cols-3 gap-3">
            {/* TODAY BY PROVIDER+KIND */}
            <div>
              <div className={`text-[10px] uppercase tracking-wider mb-1 ${t.mutedText}`}>Today by provider</div>
              <div className="space-y-0.5">
                {today.by_provider.map((p, i) => (
                  <div key={i} className={`flex items-baseline gap-2 text-[10px] font-mono ${t.secondaryText}`}>
                    <span className={`w-16 text-right ${t.primaryText}`}>${Number(p.cost_usd).toFixed(3)}</span>
                    <span className="flex-1 truncate">{p.provider} · {p.call_kind}</span>
                    <span className={t.veryMutedText}>{p.calls}×</span>
                  </div>
                ))}
                {today.by_provider.length === 0 && <div className={`text-[10px] ${t.veryMutedText}`}>no calls yet today</div>}
              </div>
            </div>

            {/* LAST 7 DAYS */}
            <div>
              <div className={`text-[10px] uppercase tracking-wider mb-1 ${t.mutedText}`}>Last 7 days</div>
              <div className="space-y-0.5">
                {last7.map((d, i) => (
                  <div key={i} className={`flex items-baseline gap-2 text-[10px] font-mono ${t.secondaryText}`}>
                    <span className={t.veryMutedText}>{String(d.day).slice(0, 10)}</span>
                    <span className={`flex-1 text-right ${t.primaryText}`}>${Number(d.total_usd).toFixed(2)}</span>
                    <span className={t.veryMutedText}>{d.calls}×</span>
                  </div>
                ))}
              </div>
            </div>

            {/* RECENT CALLS */}
            <div>
              <div className={`text-[10px] uppercase tracking-wider mb-1 ${t.mutedText}`}>Recent calls</div>
              <div className="max-h-48 overflow-y-auto space-y-0.5">
                {recent.slice(0, 20).map((r) => (
                  <div key={r.id} className={`flex items-baseline gap-2 text-[10px] font-mono ${t.secondaryText}`} title={`${r.input_tokens || 0} in / ${r.output_tokens || 0} out · ${r.latency_ms}ms`}>
                    <span className={t.veryMutedText}>{r.ts?.slice(11, 16)}</span>
                    <span className={`w-12 text-right ${t.primaryText}`}>${Number(r.cost_usd).toFixed(4)}</span>
                    <span className="flex-1 truncate">{r.provider}·{r.call_kind}</span>
                    {r.news_segment_id && <span className="text-vita-accent">#{r.news_segment_id}</span>}
                  </div>
                ))}
                {recent.length === 0 && <div className={`text-[10px] ${t.veryMutedText}`}>no calls recorded yet</div>}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ─── BufferStatusBanner ────────────────────────────────────────────
// Top-of-page indicator for the three spend buffers (breaker, budget,
// concurrency). Polls /api/admin/buffers every 15s. Renders a red
// banner the moment any buffer engages so the operator sees it before
// scrolling. The fix for "I had to find out from my credit card statement."
function BufferStatusBanner({ t }) {
  const [data, setData] = useState(null);

  useEffect(() => {
    let active = true;
    const load = async () => {
      try {
        const r = await fetch('/api/admin/buffers', { credentials: 'include' });
        if (!r.ok) return;
        const j = await r.json();
        if (active) setData(j);
      } catch { /* ignore */ }
    };
    load();
    const id = setInterval(load, 15_000);
    return () => { active = false; clearInterval(id); };
  }, []);

  if (!data) return null;

  const breakers = Object.entries(data.breaker || {});
  const openBreakers = breakers.filter(([, v]) => v.state === 'open');
  const concList = Object.entries(data.concurrency || {});
  const waiting = concList.filter(([, v]) => v.waiting > 0);
  const todayUsd = Number(data.budget?.todayUsd || 0);
  const capUsd   = Number(data.budget?.capUsd || 0);
  const budgetPct = capUsd > 0 ? (todayUsd / capUsd) * 100 : 0;
  const budgetCrossed = budgetPct >= 100;
  const budgetWarn    = budgetPct >= 80;

  const anyAlert = openBreakers.length > 0 || budgetCrossed || waiting.length > 0;
  if (!anyAlert && !budgetWarn) return null;

  return (
    <div className={`px-4 pt-2`}>
      <div className={`rounded border p-3 ${
        budgetCrossed || openBreakers.length ? 'border-red-500/50 bg-red-500/10'
        : budgetWarn ? 'border-orange-500/50 bg-orange-500/10'
        : `border-yellow-500/40 bg-yellow-500/5`
      }`}>
        <div className={`text-[11px] uppercase tracking-wider font-semibold ${
          budgetCrossed || openBreakers.length ? 'text-red-500' : budgetWarn ? 'text-orange-500' : 'text-yellow-500'
        } mb-1`}>
          ⚠ Buffer alert
        </div>
        <div className={`text-[10px] space-y-1 font-mono ${t.secondaryText}`}>
          {budgetCrossed && (
            <div>Daily budget exceeded: <span className="font-semibold">${todayUsd.toFixed(2)}</span> / ${capUsd.toFixed(2)} — autonomous LLM calls paused</div>
          )}
          {!budgetCrossed && budgetWarn && (
            <div>Daily budget {budgetPct.toFixed(0)}%: ${todayUsd.toFixed(2)} / ${capUsd.toFixed(2)} — approaching cap</div>
          )}
          {openBreakers.map(([name, v]) => (
            <div key={name}>Breaker OPEN: <span className="font-semibold">{name}</span> ({v.firesInWindow} fires, opened {v.openedAt?.slice(11, 19)}) — fallback cascades blocked</div>
          ))}
          {waiting.map(([k, v]) => (
            <div key={k}>Concurrency queue: <span className="font-semibold">{k}</span> {v.waiting} waiting, {v.inFlight}/{v.max} in flight</div>
          ))}
        </div>
      </div>
    </div>
  );
}

// ─── SignalsPanel ──────────────────────────────────────────────────
// Surfaces the user-search-behavior side of the pipeline — the data
// from trends-realtime + trends-related + trends-daily collectors,
// plus the ranker's current top-K, plus the feeder's match log.
//
// Brian explicitly asked for this view: "I do not see the monitoring
// section where we're looking for the search signals by the user
// searching on Google." This panel is the answer.
//
// Polls every 30s. Three sections:
//   - Top ranked keywords (what the matcher will try next)
//   - Recent signals collected (raw output from the three trends sources)
//   - Recent feeder attempts (which keywords matched/missed/duplicated)
// ② Demand → ③ Match — RUN-SCOPED. Reads the active run's data (ranked
// searches, collected signals, drafted segments) instead of DB-wide windows.
// Blank until a run is loaded — that's the whole point: no cross-run bleed.
// "Pulling news from" banner above the library columns — shows every source
// the library draws from with a live health dot, so it's obvious where news
// comes from and which feeds are healthy. Reads the persistent /sources list
// (not run-scoped); the PubMed + federal backbone are always-on built-ins.
// Ad-branch decision support: rank states by weight-loss search demand so you
// can see where to advertise. Reads /demand/states (google-stack Keyword
// Planner data). Collapsed by default; not run-scoped.
// ─── AdDemandPanel ─────────────────────────────────────────────────
// One-click triggers for the google-stack demand jobs (Keyword Planner per
// state / per metro, ACS demographics, priority ranking, Trends per metro) —
// so the operator runs them from here instead of the Railway Console. Each
// button POSTs /api/admin/demand/run (proxied to google-stack), then polls
// /api/admin/demand/run/:id every ~3s, mirroring CollectSignalsPanel's
// resilient-polling pattern (transient poll errors retry up to 5× before
// giving up; the job keeps running server-side regardless). Results land in the
// Demand-by-State view + the keyword_ideas tables, not here — this panel only
// shows job status + live log tail.
const DEMAND_JOBS = [
  { job: 'keywords-states', label: 'Keyword demand · by state', needsStates: true,  needsWindow: false, note: 'Google Keyword Planner per state. Blank states = all 50 + DC (slow, many paid calls).' },
  { job: 'keywords-metros', label: 'Keyword demand · by metro', needsStates: true,  needsWindow: false, note: 'Keyword Planner per metro within the given states (default TX,FL).' },
  { job: 'demographics',    label: 'Demographics',              needsStates: false, needsWindow: false, note: 'US Census ACS per-state race/ethnicity. Needs CENSUS_API_KEY.' },
  { job: 'priority',        label: 'Priority ranking',          needsStates: false, needsWindow: false, note: 'Merges keyword volume × demographics into state priority. Run keywords + demographics first.' },
  { job: 'trends-metros',   label: 'Trends · by metro',         needsStates: true,  needsWindow: true,  note: 'Google Trends momentum per metro within the given states + time window.' },
  { job: 'zip-cbsa',        label: 'ZIP → metro crosswalk',     needsStates: false, needsWindow: false, note: 'HUD ZIP↔CBSA pull for the priority metros — makes the ZIP picker metro-scoped (not state-scoped). Needs HUD_API_TOKEN.' }
];

function AdDemandJobRow({ t, def, states, window, lastById, setLast }) {
  const [running, setRunning] = useState(false);
  const [err, setErr] = useState(null);
  const [showLog, setShowLog] = useState(false);
  const last = lastById[def.job] || null;

  const run = async () => {
    setRunning(true);
    setErr(null);
    try {
      const body = { job: def.job };
      if (def.needsStates && states.trim()) body.states = states.trim();
      if (def.needsWindow) body.window = Number(window) || 30;

      const r = await fetch('/api/admin/demand/run', {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body)
      });
      const started = await r.json().catch(() => ({}));
      if (r.status === 409) {
        setErr(`already running (jobId ${started.jobId?.slice(0, 8) || '?'})`);
        setRunning(false);
        return;
      }
      if (!r.ok) throw new Error(started.error || `HTTP ${r.status}`);
      const jobId = started.jobId;
      setShowLog(true);

      // Resilient poll — ~3s tick. These pulls run minutes (Keyword Planner is
      // slow + self-throttled). Transient poll failures retry up to 5× in a
      // row; the job keeps running in google-stack regardless of this loop.
      let done = false;
      let consecutiveErrors = 0;
      const maxConsecutiveErrors = 5;
      while (!done) {
        await new Promise((res) => setTimeout(res, 3000));
        try {
          const p = await fetch(`/api/admin/demand/run/${jobId}`, { credentials: 'include' });
          if (!p.ok) {
            // 404 = rotated out of google-stack's in-memory ring. Treat as
            // terminal (we can't read final state but it's no longer tracked).
            if (p.status === 404) { done = true; break; }
            consecutiveErrors += 1;
            if (consecutiveErrors >= maxConsecutiveErrors) break;
            continue;
          }
          consecutiveErrors = 0;
          const rec = await p.json();
          setLast((m) => ({ ...m, [def.job]: rec }));
          if (rec.status !== 'running') done = true;
        } catch {
          consecutiveErrors += 1;
          if (consecutiveErrors >= maxConsecutiveErrors) {
            setErr('polling failed 5× — the job may still be running in google-stack; check back shortly');
            break;
          }
        }
      }
    } catch (e) {
      setErr(e.message);
    } finally {
      setRunning(false);
    }
  };

  const statusColor = (s) =>
    s === 'error' ? 'text-red-500'
    : s === 'done' ? 'text-emerald-500'
    : s === 'running' ? 'text-vita-accent'
    : t.veryMutedText;

  return (
    <div className={`rounded border ${t.colNeutral} p-2.5`}>
      <div className="flex items-center justify-between gap-2">
        <div className="min-w-0">
          <div className={`text-[11px] ${t.primaryText}`}>{def.label}</div>
          <div className={`text-[9px] ${t.veryMutedText} leading-snug`}>{def.note}</div>
        </div>
        <button
          onClick={run}
          disabled={running}
          className="flex-none inline-flex items-center gap-1 rounded bg-vita-accent px-2.5 py-1.5 text-vita-navy text-[11px] font-medium hover:bg-vita-bright disabled:opacity-50 transition"
        >
          {running ? <Loader2 className="w-3 h-3 animate-spin" /> : <Play className="w-3 h-3" />}
          {running ? 'Running…' : 'Run'}
        </button>
      </div>

      {err && <div className="mt-1 text-[10px] text-red-500">{err}</div>}

      {last && (
        <div className={`mt-1.5 text-[10px] ${t.mutedText} flex items-center gap-2 flex-wrap`}>
          <span>last: <span className={statusColor(last.status)}>{last.status}</span></span>
          {last.exitCode != null && last.status === 'error' && <span className={t.veryMutedText}>exit {last.exitCode}</span>}
          {last.startedAt && <span className={t.veryMutedText}>· {String(last.startedAt).slice(11, 19)}</span>}
          {Array.isArray(last.args) && last.args.length > 0 && <span className={t.veryMutedText}>· {last.args.join(' ')}</span>}
          {Array.isArray(last.logTail) && last.logTail.length > 0 && (
            <button onClick={() => setShowLog((v) => !v)} className={`${t.btn} px-1.5 py-0.5 rounded border`}>
              {showLog ? 'hide log' : `log (${last.logTail.length})`}
            </button>
          )}
        </div>
      )}

      {showLog && Array.isArray(last?.logTail) && last.logTail.length > 0 && (
        <pre className={`mt-1.5 max-h-48 overflow-y-auto rounded p-2 text-[9px] leading-snug font-mono whitespace-pre-wrap ${isLightBundle(t) ? 'bg-stone-100 text-stone-700' : 'bg-stone-900 text-stone-300'}`}>
          {last.logTail.join('\n')}
        </pre>
      )}
    </div>
  );
}

function AdDemandPanel({ t }) {
  const [open, setOpen] = useState(false);
  const [states, setStates] = useState('TX,FL');
  const [window, setWindow] = useState('30');
  const [lastById, setLast] = useState({});   // job key → last run record

  // Seed the per-job last-run state from google-stack's recent list on open so
  // a status from a prior session / another operator's run is visible.
  const loadRecent = useCallback(async () => {
    try {
      const r = await fetch('/api/admin/demand/runs', { credentials: 'include' });
      if (!r.ok) return;
      const j = await r.json();
      const byJob = {};
      for (const rec of (j.jobs || [])) {
        if (!byJob[rec.job]) byJob[rec.job] = rec;   // list is newest-first
      }
      setLast((m) => ({ ...byJob, ...m }));           // don't clobber live polling
    } catch { /* recent list is non-critical */ }
  }, []);
  useEffect(() => { if (open) loadRecent(); }, [open, loadRecent]);

  return (
    <div className="px-4 pt-3">
      <div className={`rounded border ${t.colNeutral} p-3`}>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className={`text-[11px] uppercase tracking-wider ${t.primaryText}`}>Ad demand · run jobs</div>
            <div className={`text-[10px] ${t.veryMutedText}`}>trigger the google-stack demand pulls — no Railway Console needed</div>
          </div>
          <button onClick={() => setOpen(!open)} className={`text-[10px] ${t.btn} px-2 py-1 rounded border`}>
            {open ? <ChevronUp className="w-3 h-3 inline" /> : <ChevronDown className="w-3 h-3 inline" />} {open ? 'collapse' : 'expand'}
          </button>
        </div>
        {open && (
          <div className="mt-3 space-y-2">
            <div className="grid grid-cols-2 gap-3">
              <TextField t={t} label="States (for state/metro/trends jobs)" value={states} onChange={setStates} placeholder="TX,FL" />
              <TextField t={t} label="Trends window (days)" value={window} onChange={setWindow} placeholder="30" type="number" />
            </div>
            {DEMAND_JOBS.map((def) => (
              <AdDemandJobRow
                key={def.job}
                t={t}
                def={def}
                states={states}
                window={window}
                lastById={lastById}
                setLast={setLast}
              />
            ))}
            <div className={`text-[10px] ${t.veryMutedText} pt-1`}>
              Results appear in Demand-by-State / the keyword tables — this panel only tracks job status + logs.
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function DemandByState({ t }) {
  const [data, setData] = useState(null);
  const [lang, setLang] = useState('en');
  const [open, setOpen] = useState(false);
  // ZIP drill-down: click a state → load its ZIPs ranked by fit; select to target.
  const [zipState, setZipState] = useState(null);   // 'TX'
  const [zipData, setZipData] = useState(null);
  const [targeted, setTargeted] = useState(new Set());

  const load = useCallback(() => {
    fetch(`/api/admin/demand/states?language=${lang}`, { credentials: 'include' })
      .then((r) => r.json()).then(setData).catch(() => {});
  }, [lang]);
  useEffect(() => { if (open) load(); }, [open, load]);

  const drillZips = (geo) => {
    const code = String(geo).replace(/^US-/, '');
    setZipState(code); setZipData(null); setTargeted(new Set());
    fetch(`/api/admin/demand/zips?state=${code}`, { credentials: 'include' })
      .then((r) => r.json()).then(setZipData).catch(() => {});
  };
  const toggleZip = (z) => setTargeted((prev) => {
    const n = new Set(prev); n.has(z) ? n.delete(z) : n.add(z); return n;
  });

  const states = data?.states || [];
  const max = Number(states[0]?.total_searches) || 1;
  const fmt = (n) => Number(n).toLocaleString();
  const zips = zipData?.zips || [];

  return (
    <div className="px-4 pt-3">
      <div className={`rounded border ${t.colNeutral} p-3`}>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className={`text-[11px] uppercase tracking-wider ${t.primaryText}`}>Ad demand · by state</div>
            <div className={`text-[10px] ${t.veryMutedText}`}>weight-loss search volume — where to advertise</div>
          </div>
          <div className="flex items-center gap-2">
            <select value={lang} onChange={(e) => setLang(e.target.value)}
              className={`text-[10px] px-1.5 py-1 rounded border ${isLightBundle(t) ? 'bg-white border-stone-300 text-stone-700' : 'bg-stone-900 border-stone-700 text-stone-300'}`}>
              <option value="en">EN</option>
              <option value="es">ES</option>
            </select>
            <button onClick={() => setOpen(!open)} className={`text-[10px] ${t.btn} px-2 py-1 rounded border`}>
              {open ? <ChevronUp className="w-3 h-3 inline" /> : <ChevronDown className="w-3 h-3 inline" />} {open ? 'collapse' : 'expand'}
            </button>
          </div>
        </div>
        {open && (
          <div className="mt-2">
            {data?.note && <div className={`text-[10px] ${t.veryMutedText}`}>{data.note}</div>}
            {!data && <div className={`text-[10px] ${t.veryMutedText}`}>loading…</div>}
            {data && states.length === 0 && !data.note && <div className={`text-[10px] ${t.veryMutedText}`}>no demand data — run the per-state pull in google-stack.</div>}
            <div className="text-[9px] uppercase tracking-wider mb-1 ${t.veryMutedText}">click a state to drill into its ZIPs →</div>
            <div className="grid grid-cols-2 gap-x-6 gap-y-0.5">
              {states.slice(0, 20).map((s, i) => (
                <button key={s.geo} onClick={() => drillZips(s.geo)}
                  className={`flex items-center gap-2 text-[10px] font-mono text-left ${t.secondaryText} ${zipState === s.geo.replace('US-', '') ? 'bg-vita-accent/10 rounded' : ''}`}
                  title={`avg CPC $${s.avg_cpc_usd} · ${s.keyword_count} keywords · click for ZIPs`}>
                  <span className={`w-5 text-right ${t.veryMutedText}`}>#{i + 1}</span>
                  <span className="w-28 truncate">{GEO_LABELS[s.geo] || s.geo}</span>
                  <span className="flex-1 h-2 rounded bg-vita-accent/15 overflow-hidden">
                    <span className="block h-full bg-vita-accent/60" style={{ width: `${Math.max(3, (Number(s.total_searches) / max) * 100)}%` }} />
                  </span>
                  <span className="w-16 text-right">{fmt(s.total_searches)}</span>
                  <span className={`w-12 text-right ${t.veryMutedText}`}>${s.avg_cpc_usd}</span>
                </button>
              ))}
            </div>

            {/* ZIP drill-down: a state's ZIPs ranked by fit, selectable for targeting */}
            {zipState && (
              <div className={`mt-3 pt-2 border-t ${t.divider}`}>
                <div className="flex items-center justify-between mb-1">
                  <div className={`text-[10px] uppercase tracking-wider ${t.colTitle}`}>
                    {GEO_LABELS[`US-${zipState}`] || zipState} · ZIPs by fit
                    {targeted.size > 0 && <span className="text-vita-accent"> · {targeted.size} targeted</span>}
                  </div>
                  {targeted.size > 0 && (
                    <button
                      onClick={() => navigator.clipboard?.writeText([...targeted].join(', '))}
                      className={`text-[10px] ${t.btn} px-2 py-0.5 rounded border`}>copy ZIPs</button>
                  )}
                </div>
                {zipData?.note && <div className={`text-[10px] ${t.veryMutedText}`}>{zipData.note}</div>}
                {!zipData && <div className={`text-[10px] ${t.veryMutedText}`}>loading ZIPs…</div>}
                {zipData && zips.length === 0 && !zipData.note && <div className={`text-[10px] ${t.veryMutedText}`}>no ZIP data for {zipState}.</div>}
                <div className="max-h-72 overflow-y-auto space-y-0.5">
                  {zips.slice(0, 80).map((z, i) => (
                    <label key={z.zcta} className={`flex items-center gap-2 text-[10px] font-mono cursor-pointer ${t.secondaryText}`}
                      title={`pop ${fmt(z.population)} · ${z.pct_adult_30_60}% age 30-60 · ${z.pct_male}%M/${z.pct_female}%F${z.diabetes_pct != null ? ` · diabetes ${z.diabetes_pct}%` : ''}`}>
                      <input type="checkbox" checked={targeted.has(z.zcta)} onChange={() => toggleZip(z.zcta)} />
                      <span className={`w-5 text-right ${t.veryMutedText}`}>#{i + 1}</span>
                      <span className="w-12">{z.zcta}</span>
                      <span className="w-24 truncate">{z.primary_city || '—'}</span>
                      <span className="w-16 text-right">${fmt(z.median_income)}</span>
                      <span className={`w-10 text-right ${t.veryMutedText}`}>pop {fmt(z.population)}</span>
                      <span className={`w-14 text-right ${t.veryMutedText}`} title="adult obesity prevalence (CDC PLACES)">{z.obesity_pct != null ? `ob ${Number(z.obesity_pct).toFixed(0)}%` : ''}</span>
                      <span className="w-12 text-right text-vita-accent">{Number(z.fit_score).toFixed(2)}</span>
                    </label>
                  ))}
                </div>
                <div className={`mt-1 text-[9px] ${t.veryMutedText}`}>fit = income + adult 30–60 + reach + metabolic-health need (CDC obesity/diabetes). You target ZIPs (geography); demographics/health only choose which — Google bans demographic & health-status targeting in the health vertical.</div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

// Extract the trailing 2-letter USPS state from a metro name. Google Ads labels
// each DMA by its PRIMARY state, e.g. 'Houston TX' / 'Dallas-Ft. Worth TX' — the
// last whitespace-separated token is the state code. Used by the metro→ZIP drill,
// which is STATE-SCOPED (interim): a true DMA→ZIP crosswalk is not yet in-repo.
function metroStateCode(metro) {
  const m = String(metro || '').trim().match(/([A-Za-z]{2})$/);
  return m ? m[1].toUpperCase() : null;
}

// Demand-by-Metro — sibling of DemandByState, one funnel level down (State→City).
// Reads /demand/metros (Keyword Planner, source='glp1-metro', geo = metro name).
// Click a metro → drill its ZIPs by fit. The ZIP drill is STATE-SCOPED for now
// (we map the metro to its primary state and show that state's top-fit ZIPs);
// true metro(DMA)→ZIP precision needs a ZCTA→DMA crosswalk (see note in UI).
function DemandByMetro({ t }) {
  const [data, setData] = useState(null);
  const [lang, setLang] = useState('en');
  const [open, setOpen] = useState(false);
  const [zipMetro, setZipMetro] = useState(null);   // the metro geo string clicked
  const [zipState, setZipState] = useState(null);   // its derived state code
  const [zipData, setZipData] = useState(null);
  const [targeted, setTargeted] = useState(new Set());

  const load = useCallback(() => {
    fetch(`/api/admin/demand/metros?language=${lang}`, { credentials: 'include' })
      .then((r) => r.json()).then(setData).catch(() => {});
  }, [lang]);
  useEffect(() => { if (open) load(); }, [open, load]);

  const drillZips = (metro) => {
    // Pass the metro (DMA) string; the server resolves it to a CBSA and returns
    // metro-scoped ZIPs when the HUD crosswalk is loaded, else state-scoped.
    setZipMetro(metro); setZipState(metroStateCode(metro)); setZipData(null); setTargeted(new Set());
    fetch(`/api/admin/demand/zips?metro=${encodeURIComponent(metro)}`, { credentials: 'include' })
      .then((r) => r.json()).then(setZipData).catch(() => {});
  };
  const toggleZip = (z) => setTargeted((prev) => {
    const n = new Set(prev); n.has(z) ? n.delete(z) : n.add(z); return n;
  });

  const metros = data?.metros || [];
  const max = Number(metros[0]?.total_searches) || 1;
  const fmt = (n) => Number(n).toLocaleString();
  const zips = zipData?.zips || [];

  return (
    <div className="px-4 pt-3">
      <div className={`rounded border ${t.colNeutral} p-3`}>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className={`text-[11px] uppercase tracking-wider ${t.primaryText}`}>Ad demand · by metro</div>
            <div className={`text-[10px] ${t.veryMutedText}`}>weight-loss search volume by city (DMA) — drill to ZIPs</div>
          </div>
          <div className="flex items-center gap-2">
            <select value={lang} onChange={(e) => setLang(e.target.value)}
              className={`text-[10px] px-1.5 py-1 rounded border ${isLightBundle(t) ? 'bg-white border-stone-300 text-stone-700' : 'bg-stone-900 border-stone-700 text-stone-300'}`}>
              <option value="en">EN</option>
              <option value="es">ES</option>
            </select>
            <button onClick={() => setOpen(!open)} className={`text-[10px] ${t.btn} px-2 py-1 rounded border`}>
              {open ? <ChevronUp className="w-3 h-3 inline" /> : <ChevronDown className="w-3 h-3 inline" />} {open ? 'collapse' : 'expand'}
            </button>
          </div>
        </div>
        {open && (
          <div className="mt-2">
            {data?.note && <div className={`text-[10px] ${t.veryMutedText}`}>{data.note}</div>}
            {!data && <div className={`text-[10px] ${t.veryMutedText}`}>loading…</div>}
            {data && metros.length === 0 && !data.note && <div className={`text-[10px] ${t.veryMutedText}`}>no metro demand — run the per-metro pull (google-stack glp1:metros).</div>}
            <div className={`text-[9px] uppercase tracking-wider mb-1 ${t.veryMutedText}`}>click a metro to drill into its ZIPs →</div>
            <div className="grid grid-cols-2 gap-x-6 gap-y-0.5">
              {metros.slice(0, 24).map((m, i) => (
                <button key={m.geo} onClick={() => drillZips(m.geo)}
                  className={`flex items-center gap-2 text-[10px] font-mono text-left ${t.secondaryText} ${zipMetro === m.geo ? 'bg-vita-accent/10 rounded' : ''}`}
                  title={`avg CPC $${m.avg_cpc_usd} · ${m.keyword_count} keywords · click for ZIPs`}>
                  <span className={`w-5 text-right ${t.veryMutedText}`}>#{i + 1}</span>
                  <span className="w-32 truncate">{m.geo}</span>
                  <span className="flex-1 h-2 rounded bg-vita-accent/15 overflow-hidden">
                    <span className="block h-full bg-vita-accent/60" style={{ width: `${Math.max(3, (Number(m.total_searches) / max) * 100)}%` }} />
                  </span>
                  <span className="w-16 text-right">{fmt(m.total_searches)}</span>
                  <span className={`w-12 text-right ${t.veryMutedText}`}>${m.avg_cpc_usd}</span>
                </button>
              ))}
            </div>

            {/* ZIP drill-down (state-scoped interim — see note below). */}
            {zipMetro && (
              <div className={`mt-3 pt-2 border-t ${t.divider}`}>
                <div className="flex items-center justify-between mb-1">
                  <div className={`text-[10px] uppercase tracking-wider ${t.colTitle}`}>
                    {zipMetro} · ZIPs by fit
                    {zipData?.scope === 'cbsa'
                      ? <span className="text-vita-accent"> (metro-scoped)</span>
                      : zipState ? ` (${zipState}-scoped)` : ''}
                    {targeted.size > 0 && <span className="text-vita-accent"> · {targeted.size} targeted</span>}
                  </div>
                  {targeted.size > 0 && (
                    <button
                      onClick={() => navigator.clipboard?.writeText([...targeted].join(', '))}
                      className={`text-[10px] ${t.btn} px-2 py-0.5 rounded border`}>copy ZIPs</button>
                  )}
                </div>
                {zipData?.scope === 'cbsa' ? (
                  <div className={`text-[10px] ${t.veryMutedText} mb-1`}>
                    Metro-scoped: ZIPs in the <span className="text-vita-accent">{zipData.cbsa_name || `CBSA ${zipData.cbsa_code}`}</span> metro (HUD ZIP↔CBSA crosswalk), ranked by fit.
                  </div>
                ) : zipState && (
                  <div className={`text-[10px] ${t.veryMutedText} mb-1`}>
                    {zipData?.note
                      ? zipData.note
                      : <>Interim: showing top-fit ZIPs in <span className="text-vita-accent">{GEO_LABELS[`US-${zipState}`] || zipState}</span> (the metro's primary state), not just the {zipMetro} metro. Run the “ZIP → metro crosswalk” job (HUD) for metro-scoped precision.</>}
                  </div>
                )}
                {zipData?.note && <div className={`text-[10px] ${t.veryMutedText}`}>{zipData.note}</div>}
                {!zipData && <div className={`text-[10px] ${t.veryMutedText}`}>loading ZIPs…</div>}
                {zipData && zips.length === 0 && !zipData.note && <div className={`text-[10px] ${t.veryMutedText}`}>no ZIP data for {zipState}.</div>}
                <div className="max-h-72 overflow-y-auto space-y-0.5">
                  {zips.slice(0, 80).map((z, i) => (
                    <label key={z.zcta} className={`flex items-center gap-2 text-[10px] font-mono cursor-pointer ${t.secondaryText}`}
                      title={`pop ${fmt(z.population)} · ${z.pct_adult_30_60}% age 30-60 · ${z.pct_male}%M/${z.pct_female}%F${z.diabetes_pct != null ? ` · diabetes ${z.diabetes_pct}%` : ''}`}>
                      <input type="checkbox" checked={targeted.has(z.zcta)} onChange={() => toggleZip(z.zcta)} />
                      <span className={`w-5 text-right ${t.veryMutedText}`}>#{i + 1}</span>
                      <span className="w-12">{z.zcta}</span>
                      <span className="w-24 truncate">{z.primary_city || '—'}</span>
                      <span className="w-16 text-right">${fmt(z.median_income)}</span>
                      <span className={`w-10 text-right ${t.veryMutedText}`}>pop {fmt(z.population)}</span>
                      <span className={`w-14 text-right ${t.veryMutedText}`} title="adult obesity prevalence (CDC PLACES)">{z.obesity_pct != null ? `ob ${Number(z.obesity_pct).toFixed(0)}%` : ''}</span>
                      <span className="w-12 text-right text-vita-accent">{Number(z.fit_score).toFixed(2)}</span>
                    </label>
                  ))}
                </div>
                <div className={`mt-1 text-[9px] ${t.veryMutedText}`}>fit = income + adult 30–60 + reach + metabolic-health need (CDC obesity/diabetes). You target ZIPs (geography); demographics/health only choose which — Google bans demographic & health-status targeting in the health vertical.</div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

// Trends · momentum — top metros by RELATIVE interest + rising related queries
// from google-stack's trends:metros pull (tables trends_metro + trends_rising).
// "interest" is a 0–100 RELATIVE index (NOT volume); pair with Demand-by-Metro
// (Keyword Planner) for volume. Window selector: 7 / 30 / 90 days.
function TrendsMomentum({ t }) {
  const [data, setData] = useState(null);
  const [lang, setLang] = useState('en');
  const [windowDays, setWindowDays] = useState('30');
  const [open, setOpen] = useState(false);

  const load = useCallback(() => {
    setData(null);
    fetch(`/api/admin/trends/metros?language=${lang}&window=${windowDays}`, { credentials: 'include' })
      .then((r) => r.json()).then(setData).catch(() => {});
  }, [lang, windowDays]);
  useEffect(() => { if (open) load(); }, [open, load]);

  const metros = data?.metros || [];
  const rising = data?.rising || [];
  const max = Number(metros[0]?.avg_interest) || 100;

  return (
    <div className="px-4 pt-3">
      <div className={`rounded border ${t.colNeutral} p-3`}>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className={`text-[11px] uppercase tracking-wider ${t.primaryText}`}>Trends · momentum</div>
            <div className={`text-[10px] ${t.veryMutedText}`}>what's heating up, by metro + rising queries (relative 0–100, not volume)</div>
          </div>
          <div className="flex items-center gap-2">
            <select value={windowDays} onChange={(e) => setWindowDays(e.target.value)}
              className={`text-[10px] px-1.5 py-1 rounded border ${isLightBundle(t) ? 'bg-white border-stone-300 text-stone-700' : 'bg-stone-900 border-stone-700 text-stone-300'}`}>
              <option value="7">7d</option>
              <option value="30">30d</option>
              <option value="90">90d</option>
            </select>
            <select value={lang} onChange={(e) => setLang(e.target.value)}
              className={`text-[10px] px-1.5 py-1 rounded border ${isLightBundle(t) ? 'bg-white border-stone-300 text-stone-700' : 'bg-stone-900 border-stone-700 text-stone-300'}`}>
              <option value="en">EN</option>
              <option value="es">ES</option>
            </select>
            <button onClick={() => setOpen(!open)} className={`text-[10px] ${t.btn} px-2 py-1 rounded border`}>
              {open ? <ChevronUp className="w-3 h-3 inline" /> : <ChevronDown className="w-3 h-3 inline" />} {open ? 'collapse' : 'expand'}
            </button>
          </div>
        </div>
        {open && (
          <div className="mt-2 grid grid-cols-2 gap-4">
            {/* Top metros by relative interest */}
            <div>
              <div className={`text-[9px] uppercase tracking-wider mb-1 ${t.veryMutedText}`}>top metros · last {windowDays}d (rel. interest 0–100)</div>
              {data?.note && <div className={`text-[10px] ${t.veryMutedText}`}>{data.note}</div>}
              {!data && <div className={`text-[10px] ${t.veryMutedText}`}>loading…</div>}
              {data && metros.length === 0 && !data.note && <div className={`text-[10px] ${t.veryMutedText}`}>no momentum data — run google-stack trends:metros.</div>}
              <div className="max-h-72 overflow-y-auto space-y-0.5">
                {metros.map((m, i) => (
                  <div key={m.metro} className={`flex items-center gap-2 text-[10px] font-mono ${t.secondaryText}`}
                    title={`peak ${m.peak_interest} · ${m.term_count} terms`}>
                    <span className={`w-5 text-right ${t.veryMutedText}`}>#{i + 1}</span>
                    <span className="w-28 truncate">{m.metro}</span>
                    <span className="flex-1 h-2 rounded bg-vita-accent/15 overflow-hidden">
                      <span className="block h-full bg-vita-accent/60" style={{ width: `${Math.max(3, (Number(m.avg_interest) / max) * 100)}%` }} />
                    </span>
                    <span className="w-10 text-right text-vita-accent">{m.avg_interest}</span>
                  </div>
                ))}
              </div>
            </div>
            {/* Rising related queries */}
            <div>
              <div className={`text-[9px] uppercase tracking-wider mb-1 ${t.veryMutedText}`}>rising queries · last {windowDays}d</div>
              {data && rising.length === 0 && !data.note && <div className={`text-[10px] ${t.veryMutedText}`}>no rising queries.</div>}
              <div className="max-h-72 overflow-y-auto space-y-0.5">
                {rising.map((r, i) => (
                  <div key={`${r.query}-${r.state}-${i}`} className={`flex items-center gap-2 text-[10px] font-mono ${t.secondaryText}`}>
                    <span className={`w-5 text-right ${t.veryMutedText}`}>#{i + 1}</span>
                    <span className="flex-1 truncate">{r.query}</span>
                    <span className={`w-8 text-right ${t.veryMutedText}`}>{r.state}</span>
                    <span className="w-16 text-right text-vita-accent">{r.formatted_value || r.value || '↑'}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// Branch 2: generate ad creatives (Google Search / Meta / YouTube) from a hot
// keyword, in the educational "answer your questions" framing (policy-safe by
// construction). Review here; launch is a later, cost-gated step.
function AdDrafts({ t }) {
  const [open, setOpen] = useState(false);
  const [keyword, setKeyword] = useState('');
  const [geo, setGeo] = useState('');
  const [language, setLanguage] = useState('en');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const [drafts, setDrafts] = useState([]);

  const load = useCallback(() => {
    fetch('/api/admin/ads?limit=20', { credentials: 'include' })
      .then((r) => r.json()).then((j) => setDrafts(j.drafts || j.ads || [])).catch(() => {});
  }, []);
  useEffect(() => { if (open) load(); }, [open, load]);

  const generate = async () => {
    if (!keyword.trim()) return;
    setBusy(true); setErr(null);
    try {
      const r = await fetch('/api/admin/ads/generate', {
        method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ keyword: keyword.trim(), geo: geo || null, language })
      });
      if (!r.ok) throw new Error(await r.text());
      setKeyword('');
      load();
    } catch (e) { setErr(e.message.slice(0, 160)); }
    finally { setBusy(false); }
  };

  const setStatus = async (id, status) => {
    await fetch(`/api/admin/ads/${id}/status`, {
      method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ status })
    }).catch(() => {});
    load();
  };

  return (
    <div className="px-4 pt-3">
      <div className={`rounded border ${t.colNeutral} p-3`}>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className={`text-[11px] uppercase tracking-wider ${t.primaryText}`}>Ad drafts · Search / Meta / YouTube</div>
            <div className={`text-[10px] ${t.veryMutedText}`}>educational framing — review then launch (cost-gated)</div>
          </div>
          <button onClick={() => setOpen(!open)} className={`text-[10px] ${t.btn} px-2 py-1 rounded border`}>
            {open ? <ChevronUp className="w-3 h-3 inline" /> : <ChevronDown className="w-3 h-3 inline" />} {open ? 'collapse' : 'expand'}
          </button>
        </div>
        {open && (
          <div className="mt-2">
            <div className="flex items-center gap-2 mb-2">
              <input value={keyword} onChange={(e) => setKeyword(e.target.value)} placeholder="keyword (e.g. new weight loss drug)"
                className={`flex-1 px-2 py-1.5 rounded border text-xs ${isLightBundle(t) ? 'bg-white border-stone-300 text-stone-900 placeholder-stone-400' : 'bg-stone-900 border-stone-800 text-stone-100 placeholder-stone-600'}`} />
              <input value={geo} onChange={(e) => setGeo(e.target.value)} placeholder="geo (opt)"
                className={`w-24 px-2 py-1.5 rounded border text-xs ${isLightBundle(t) ? 'bg-white border-stone-300 text-stone-900 placeholder-stone-400' : 'bg-stone-900 border-stone-800 text-stone-100 placeholder-stone-600'}`} />
              <select value={language} onChange={(e) => setLanguage(e.target.value)}
                className={`text-xs px-1.5 py-1.5 rounded border ${isLightBundle(t) ? 'bg-white border-stone-300 text-stone-700' : 'bg-stone-900 border-stone-700 text-stone-300'}`}>
                <option value="en">EN</option><option value="es">ES</option>
              </select>
              <button onClick={generate} disabled={busy || !keyword.trim()}
                className="inline-flex items-center gap-1 rounded bg-vita-accent px-3 py-1.5 text-vita-navy text-xs font-medium hover:bg-vita-bright disabled:opacity-50 transition">
                {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <Zap className="w-3 h-3" />} Generate
              </button>
            </div>
            {err && <div className="text-[10px] text-red-500 mb-1">{err}</div>}
            {drafts.length === 0 && <div className={`text-[10px] ${t.veryMutedText}`}>no ad drafts yet — generate one from a keyword.</div>}
            <div className="space-y-2">
              {drafts.map((d) => <AdDraftCard key={d.id} t={t} d={d} onStatus={setStatus} />)}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function AdDraftCard({ t, d, onStatus }) {
  const f = d.formats || {};
  const gs = f.google_search || {};
  const meta = f.meta || {};
  const yt = f.youtube || {};
  const statusColor = d.status === 'approved' ? 'text-emerald-500' : d.status === 'rejected' ? 'text-red-500' : d.status === 'launched' ? 'text-vita-accent' : t.mutedText;
  return (
    <div className={`rounded border ${t.divider} p-2`}>
      <div className="flex items-center justify-between mb-1">
        <div className={`text-[11px] ${t.primaryText}`}>
          <Zap className="w-3 h-3 inline text-vita-accent" /> {d.keyword}
          <span className={`ml-2 text-[10px] ${t.veryMutedText}`}>{d.language}{d.geo ? ` · ${d.geo}` : ''}</span>
          <span className={`ml-2 text-[10px] ${statusColor}`}>· {d.status}</span>
        </div>
        <div className="flex items-center gap-1">
          <button onClick={() => onStatus(d.id, 'approved')} className={`text-[10px] px-2 py-0.5 rounded border ${t.btn}`}>approve</button>
          <button onClick={() => onStatus(d.id, 'rejected')} className={`text-[10px] px-2 py-0.5 rounded border ${t.btn}`}>reject</button>
        </div>
      </div>
      <div className="grid grid-cols-3 gap-3 text-[10px]">
        {/* Google Search */}
        <div>
          <div className={`uppercase tracking-wider mb-1 ${t.colTitle}`}>Google Search</div>
          {(gs.headlines || []).slice(0, 6).map((h, i) => <div key={i} className={t.secondaryText}>• {h}</div>)}
          {(gs.descriptions || []).map((dsc, i) => <div key={i} className={`mt-1 ${t.veryMutedText}`}>{dsc}</div>)}
          {gs.final_url && <div className={`mt-1 ${t.veryMutedText} truncate`}>{gs.final_url}</div>}
        </div>
        {/* Meta */}
        <div>
          <div className={`uppercase tracking-wider mb-1 ${t.colTitle}`}>Meta</div>
          <div className={t.secondaryText}>{meta.headline}</div>
          <div className={`mt-1 ${t.secondaryText}`}>{meta.primary_text}</div>
          <div className={`mt-1 ${t.veryMutedText}`}>{meta.description}</div>
          <div className={`mt-1 ${t.veryMutedText}`}>cta: {meta.cta}</div>
          {meta.image_concept && <div className={`mt-1 italic ${t.veryMutedText}`}>img: {meta.image_concept}</div>}
        </div>
        {/* YouTube */}
        <div>
          <div className={`uppercase tracking-wider mb-1 ${t.colTitle}`}>YouTube</div>
          <div className={t.secondaryText}>{yt.hook}</div>
          <div className={`mt-1 ${t.veryMutedText} line-clamp-4`}>{yt.script}</div>
          <div className={`mt-1 ${t.veryMutedText}`}>cta: {yt.cta} · {(yt.visual_beats || []).length} beats</div>
        </div>
      </div>
    </div>
  );
}

function SourcesBanner({ t }) {
  const [sources, setSources] = useState([]);
  useEffect(() => {
    let active = true;
    const load = () => fetch('/api/admin/sources', { credentials: 'include' })
      .then((r) => r.json()).then((j) => { if (active) setSources(j.sources || []); }).catch(() => {});
    load();
    const id = setInterval(load, 60_000);
    return () => { active = false; clearInterval(id); };
  }, []);

  const enabled = sources.filter((s) => s.enabled);
  const dot = (s) => s.last_error ? 'bg-red-500' : (s.last_success_at || s.last_fetched_at) ? 'bg-emerald-500' : 'bg-stone-400';
  const tip = (s) => s.last_error ? `error: ${s.last_error}`
    : s.last_success_at ? `ok · last success ${String(s.last_success_at).slice(0, 16).replace('T', ' ')}`
    : 'not fetched yet';

  return (
    <div className="px-4 pt-3">
      <div className={`rounded border ${t.colNeutral} px-3 py-2`}>
        <div className="flex items-center flex-wrap gap-x-3 gap-y-1">
          <span className={`text-[10px] uppercase tracking-wider ${t.mutedText}`}>Pulling news from</span>
          {/* Always-on backbone (not in news_sources) */}
          {['PubMed', 'FDA / CDC / NIH'].map((label) => (
            <span key={label} className={`inline-flex items-center gap-1 text-[10px] ${t.secondaryText}`}>
              <span className="w-2 h-2 rounded-full bg-emerald-500" />{label}
            </span>
          ))}
          {enabled.map((s) => (
            <span key={s.id} className={`inline-flex items-center gap-1 text-[10px] ${t.secondaryText}`} title={tip(s)}>
              <span className={`w-2 h-2 rounded-full ${dot(s)}`} />{s.label}
            </span>
          ))}
          {enabled.length === 0 && <span className={`text-[10px] ${t.veryMutedText}`}>no custom feeds enabled</span>}
        </div>
      </div>
    </div>
  );
}

function SignalsPanel({ t, run, runData }) {
  const [expanded, setExpanded] = useState(true);

  const ranked   = runData?.ranked   || [];
  const signals  = runData?.signals  || [];
  const segments = runData?.segments || [];

  // by-source breakdown for this run's collected signals.
  const bySource = {};
  signals.forEach((s) => {
    const k = s.source + (s.metric ? `/${s.metric}` : '');
    bySource[k] = (bySource[k] || 0) + 1;
  });

  return (
    <div className="px-4 pt-2 pb-1">
      <div className={`rounded border ${t.colNeutral} p-3`}>
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-3">
            <Search className={`w-3 h-3 ${t.mutedText}`} />
            <div className={`text-[11px] uppercase tracking-wider ${t.primaryText}`}>② Demand → ③ Match · search signals</div>
            {run ? (
              <div className={`text-[10px] ${t.veryMutedText}`}>
                {(run.geos || []).map((g) => GEO_LABELS[g] || g).join(', ') || run.kind} · {ranked.length} ranked · {signals.length} signals · {segments.length} drafted
              </div>
            ) : (
              <div className={`text-[10px] ${t.emptyText}`}>no run loaded</div>
            )}
          </div>
          <button onClick={() => setExpanded(!expanded)} className={`text-[10px] ${t.btn} px-2 py-1 rounded border`}>
            {expanded ? <ChevronUp className="w-3 h-3 inline" /> : <ChevronDown className="w-3 h-3 inline" />} {expanded ? 'collapse' : 'expand'}
          </button>
        </div>

        {!run && (
          <div className={`text-xs ${t.emptyText} py-3`}>
            Nothing loaded yet. Click <span className={t.primaryText}>Collect Signals</span> above to start a new run,
            or open <span className={t.primaryText}>History</span> to load a previous one.
          </div>
        )}

        {run && expanded && (
          <div className="grid grid-cols-3 gap-3">
            {/* TOP RANKED — this run's demand ranking */}
            <div>
              <div className={`text-[10px] uppercase tracking-wider mb-1 ${t.mutedText}`}>Top ranked (demand)</div>
              <div className="max-h-72 overflow-y-auto space-y-0.5">
                {ranked.length === 0 && <div className={`text-[10px] ${t.veryMutedText}`}>no ranked keywords for this run</div>}
                {ranked.map((r, i) => (
                  <div key={i} className={`flex items-baseline gap-2 text-[10px] font-mono ${t.secondaryText}`}>
                    <span className={`w-6 text-right ${t.primaryText}`}>#{r.rank}</span>
                    <span className={`w-10 ${t.veryMutedText}`}>{Number(r.score).toFixed(2)}</span>
                    <span className="flex-1 truncate">{r.keyword}</span>
                    <span className={t.veryMutedText}>{r.persona}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* COLLECTED SIGNALS — this run's raw collector output */}
            <div>
              <div className={`text-[10px] uppercase tracking-wider mb-1 ${t.mutedText}`}>Signals collected</div>
              <div className={`text-[10px] ${t.veryMutedText} mb-1`}>
                by source: {Object.entries(bySource).map(([k, v]) => `${k}=${v}`).join(' · ') || 'none'}
              </div>
              <div className="max-h-60 overflow-y-auto space-y-0.5">
                {signals.length === 0 && <div className={`text-[10px] ${t.veryMutedText}`}>no signals in this run</div>}
                {signals.slice(0, 60).map((s, i) => (
                  <div key={i} className={`flex items-baseline gap-2 text-[10px] font-mono ${t.secondaryText}`} title={s.meta ? JSON.stringify(s.meta).slice(0, 200) : ''}>
                    <span className={t.veryMutedText}>{s.ts?.slice(11, 16)}</span>
                    {s.meta?.geo && <span className={`${t.veryMutedText} truncate max-w-[64px]`}>{GEO_LABELS[s.meta.geo] || s.meta.geo}</span>}
                    <span className="flex-1 truncate">{s.keyword}</span>
                    <span className={t.veryMutedText}>{Number(s.value).toFixed(0)}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* DRAFTED — segments this run produced (match → script) */}
            <div>
              <div className={`text-[10px] uppercase tracking-wider mb-1 ${t.mutedText}`}>Drafted this run</div>
              <div className="max-h-72 overflow-y-auto space-y-0.5">
                {segments.length === 0 && <div className={`text-[10px] ${t.veryMutedText}`}>no segments drafted</div>}
                {segments.map((s) => (
                  <div key={s.id} className={`flex items-baseline gap-2 text-[10px] font-mono ${t.secondaryText}`} title={s.news_item_title || ''}>
                    <span className="text-vita-accent">#{s.id}</span>
                    <span className={t.veryMutedText}>{s.language || '-'}</span>
                    <span className="flex-1 truncate">{s.matched_keyword || s.news_item_title}</span>
                    <span className={t.veryMutedText}>{s.status}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// History drawer — durable list of past runs (from pipeline_runs). Selecting
// one loads it into the dashboard. This is how you reach work from any run
// without the dashboard ever showing cross-run accumulation.
function HistoryDrawer({ t, runs, activeRunId, onSelect, onClose }) {
  return (
    <div className={`border-b ${t.header} px-6 py-3`}>
      <div className="flex items-center justify-between mb-2">
        <div className={`text-[11px] uppercase tracking-wider ${t.primaryText}`}>History · load a previous run</div>
        <button onClick={onClose} className={`text-[10px] ${t.btn} px-2 py-1 rounded border`}>close</button>
      </div>
      {runs.length === 0 && <div className={`text-xs ${t.emptyText}`}>No runs yet. Start one with Collect Signals.</div>}
      <div className="max-h-56 overflow-y-auto space-y-1">
        {runs.map((r) => {
          const geoLabel = (r.geos || []).map((g) => GEO_LABELS[g] || g).join(', ') || r.kind;
          const isActive = r.id === activeRunId;
          const s = r.summary || {};
          return (
            <button
              key={r.id}
              onClick={() => onSelect(r.id)}
              className={`w-full text-left flex items-center gap-3 px-2 py-1.5 rounded border text-[11px] ${isActive ? 'border-vita-accent bg-vita-accent/10' : t.btn}`}
            >
              <span className={`w-3 h-3 rounded-full ${r.status === 'completed' ? 'bg-emerald-500' : r.status === 'failed' ? 'bg-red-500' : 'bg-yellow-500'}`} />
              <span className={`font-medium ${t.primaryText}`}>{geoLabel}</span>
              <span className={t.veryMutedText}>{String(r.started_at || '').slice(0, 16).replace('T', ' ')}</span>
              <span className={`flex-1 text-right ${t.veryMutedText}`}>
                {s.collected != null ? `${s.collected} signals · ` : ''}{s.feed?.generated != null ? `${s.feed.generated} drafted` : r.status}
              </span>
            </button>
          );
        })}
      </div>
    </div>
  );
}

// ─── CollectSignalsPanel ───────────────────────────────────────────
// User-search-signal collector — the OTHER pipeline. Fires the trends
// realtime + related + daily collectors → ranker → feeder. Companion to
// RunPipelinePanel (which handles news ingest → score → commentary).
//
// Brian's framing: "If I click Run Pipeline will this start the monitoring
// process?" — no, that was the editorial chain. THIS is the Google search
// signal chain. Made an explicit button so the difference is visible.

// Geo options for signal collection. Google Trends granularity floor is
// state then metro/DMA — sub-metro (zip/city) lives in the ads-targeting
// layer, not signals. Max 3 geos per run (server enforces too) — each geo
// multiplies the Trends calls and Google 429s aggressively.
const MAX_SIGNAL_GEOS = 3;
const US_STATES = [
  ['US-AL','Alabama'],['US-AK','Alaska'],['US-AZ','Arizona'],['US-AR','Arkansas'],['US-CA','California'],
  ['US-CO','Colorado'],['US-CT','Connecticut'],['US-DE','Delaware'],['US-DC','District of Columbia'],['US-FL','Florida'],
  ['US-GA','Georgia'],['US-HI','Hawaii'],['US-ID','Idaho'],['US-IL','Illinois'],['US-IN','Indiana'],
  ['US-IA','Iowa'],['US-KS','Kansas'],['US-KY','Kentucky'],['US-LA','Louisiana'],['US-ME','Maine'],
  ['US-MD','Maryland'],['US-MA','Massachusetts'],['US-MI','Michigan'],['US-MN','Minnesota'],['US-MS','Mississippi'],
  ['US-MO','Missouri'],['US-MT','Montana'],['US-NE','Nebraska'],['US-NV','Nevada'],['US-NH','New Hampshire'],
  ['US-NJ','New Jersey'],['US-NM','New Mexico'],['US-NY','New York'],['US-NC','North Carolina'],['US-ND','North Dakota'],
  ['US-OH','Ohio'],['US-OK','Oklahoma'],['US-OR','Oregon'],['US-PA','Pennsylvania'],['US-RI','Rhode Island'],
  ['US-SC','South Carolina'],['US-SD','South Dakota'],['US-TN','Tennessee'],['US-TX','Texas'],['US-UT','Utah'],
  ['US-VT','Vermont'],['US-VA','Virginia'],['US-WA','Washington'],['US-WV','West Virginia'],['US-WI','Wisconsin'],
  ['US-WY','Wyoming']
];
// Major US metros by Nielsen DMA code (verified against Google Trends).
// Sorted alphabetically by label. Houston = US-TX-618.
const US_METROS = [
  ['US-GA-524','Atlanta, GA'],['US-TX-635','Austin, TX'],['US-MD-512','Baltimore, MD'],
  ['US-MA-506','Boston, MA'],['US-NC-517','Charlotte, NC'],['US-IL-602','Chicago, IL'],
  ['US-OH-515','Cincinnati, OH'],['US-OH-510','Cleveland–Akron, OH'],['US-OH-535','Columbus, OH'],
  ['US-TX-623','Dallas–Ft. Worth, TX'],['US-CO-751','Denver, CO'],['US-MI-505','Detroit, MI'],
  ['US-CT-533','Hartford–New Haven, CT'],['US-TX-618','Houston, TX'],['US-IN-527','Indianapolis, IN'],
  ['US-FL-561','Jacksonville, FL'],['US-MO-616','Kansas City, MO'],['US-NV-839','Las Vegas, NV'],
  ['US-CA-803','Los Angeles, CA'],['US-FL-528','Miami–Ft. Lauderdale, FL'],['US-MN-613','Minneapolis–St. Paul, MN'],
  ['US-TN-659','Nashville, TN'],['US-NY-501','New York, NY'],['US-FL-534','Orlando, FL'],
  ['US-PA-504','Philadelphia, PA'],['US-AZ-753','Phoenix, AZ'],['US-PA-508','Pittsburgh, PA'],
  ['US-OR-820','Portland, OR'],['US-NC-560','Raleigh–Durham, NC'],['US-CA-862','Sacramento, CA'],
  ['US-UT-770','Salt Lake City, UT'],['US-TX-641','San Antonio, TX'],['US-CA-825','San Diego, CA'],
  ['US-CA-807','San Francisco–Oakland–San Jose, CA'],['US-WA-819','Seattle–Tacoma, WA'],['US-MO-609','St. Louis, MO'],
  ['US-FL-539','Tampa–St. Pete, FL'],['US-DC-511','Washington, DC']
];
const GEO_LABELS = Object.fromEntries([...US_STATES, ...US_METROS]);

function CollectSignalsPanel({ t, isLight, onRefresh, onRunComplete }) {
  const [dryRun, setDryRun] = useState(false);
  const [skipCollect, setSkipCollect] = useState(false);
  const [geoMode, setGeoMode] = useState('US');      // 'US' | 'states' | 'metros'
  const [picked, setPicked] = useState([]);          // full geo codes, max 3
  const [running, setRunning] = useState(false);
  const [lastResult, setLastResult] = useState(null);
  const [err, setErr] = useState(null);

  // Switching mode clears the picks (state codes and metro codes don't mix).
  const changeMode = (m) => { setGeoMode(m); setPicked([]); };
  const geoOptions = geoMode === 'metros' ? US_METROS : US_STATES;
  const geos = geoMode === 'US' ? ['US'] : picked;
  const needsPick = geoMode !== 'US' && picked.length === 0;

  // ── News-library refresh (formerly the standalone "Run Pipeline" panel) ──
  // The matcher draws from the news_items library; this manually tops it up
  // (RSS ingest → Claude score → auto-approve). Normally the cron keeps it
  // fresh — this is the manual override. Demoted to a secondary action so the
  // trend search stays the single starting point of the flow.
  const [refreshing, setRefreshing] = useState(false);
  const [refreshMsg, setRefreshMsg] = useState(null);
  const refreshLibrary = async () => {
    setRefreshing(true);
    setRefreshMsg(null);
    try {
      const r = await fetch('/api/admin/pipeline/run', {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ vertical: 'weight-loss', minScore: 0.65, limit: 3, persona: 'professional' })
      });
      if (!r.ok) throw new Error(await r.text());
      const body = await r.json();
      let done = false, errs = 0;
      while (!done) {
        await new Promise((res) => setTimeout(res, 3000));
        try {
          const p = await fetch(`/api/admin/runs/${body.runId}`, { credentials: 'include' });
          if (!p.ok) { if (p.status === 404) { done = true; onRefresh?.(); break; } if (++errs >= 5) break; continue; }
          errs = 0;
          const run = await p.json();
          setRefreshMsg(`library: ${run.status}`);
          if (run.status !== 'running') { done = true; onRefresh?.(); }
        } catch { if (++errs >= 5) { setRefreshMsg('polling stalled — may still be running'); break; } }
      }
    } catch (e) {
      setRefreshMsg(`failed: ${e.message.slice(0, 80)}`);
    } finally {
      setRefreshing(false);
    }
  };

  const run = async () => {
    setRunning(true);
    setLastResult(null);
    setErr(null);
    try {
      const r = await fetch('/api/admin/signals/run', {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ dryRun, skipCollect, geos })
      });
      if (!r.ok) throw new Error(await r.text());
      const body = await r.json();
      // Poll until terminal status. ~3s tick; cycle typically 30-120s.
      // Transient network errors during polling do NOT bail — they retry
      // up to 5 in a row before giving up. The actual run keeps executing
      // server-side regardless; this loop is just for UI status display.
      let done = false;
      let consecutiveErrors = 0;
      const maxConsecutiveErrors = 5;
      while (!done) {
        await new Promise((res) => setTimeout(res, 3000));
        try {
          const p = await fetch(`/api/admin/runs/${body.runId}`, { credentials: 'include' });
          if (!p.ok) {
            // 404 means the run rotated out of the in-memory ring buffer.
            // Treat that as success — the backend completed and we just
            // can't read the final state. Refresh the dashboard to pick
            // up any new segments.
            if (p.status === 404) { done = true; onRefresh?.(); onRunComplete?.(body.runId); break; }
            consecutiveErrors += 1;
            if (consecutiveErrors >= maxConsecutiveErrors) break;
            continue;
          }
          consecutiveErrors = 0;
          const run = await p.json();
          setLastResult(run);
          if (run.status !== 'running') {
            done = true;
            onRefresh?.();
            // Load this run into the dashboard (run-scoped view).
            onRunComplete?.(body.runId);
          }
        } catch (pollErr) {
          // Network glitch — keep trying. The backend is unaffected.
          consecutiveErrors += 1;
          if (consecutiveErrors >= maxConsecutiveErrors) {
            setErr(`polling failed ${maxConsecutiveErrors}× in a row — the cycle may still be running server-side; refresh the dashboard to check`);
            break;
          }
        }
      }
    } catch (e) {
      setErr(e.message);
    } finally {
      setRunning(false);
    }
  };


  const summary = lastResult?.result;
  return (
    <PanelCard t={t} title="① Discover trends → match → draft">
      <div className={`text-[10px] ${t.mutedText} mb-1`}>
        Pulls live Google Trends (realtime + related queries + daily) → ranks → matches news → drafts EN+ES commentary.
      </div>
      <label className={`flex items-center gap-2 text-[10px] ${t.mutedText} cursor-pointer`}>
        <input type="checkbox" checked={dryRun} onChange={(e) => setDryRun(e.target.checked)} />
        dry run (no commentary calls)
      </label>
      <label className={`flex items-center gap-2 text-[10px] ${t.mutedText} cursor-pointer`}>
        <input type="checkbox" checked={skipCollect} onChange={(e) => setSkipCollect(e.target.checked)} />
        skip collect (just rerank+feed)
      </label>
      {/* Geo scope — nationwide, up to MAX_SIGNAL_GEOS states, or metros.
          Trends floor is metro/DMA level; zip targeting belongs to the
          ads layer. */}
      <div className="mt-2">
        <SelectField
          t={t}
          label="Signal geo"
          value={geoMode}
          onChange={changeMode}
          options={[
            { value: 'US', label: 'Nationwide (US)' },
            { value: 'states', label: 'By state(s)' },
            { value: 'metros', label: 'By metro / city' }
          ]}
        />
        {geoMode !== 'US' && (
          <>
            <select
              value=""
              disabled={picked.length >= MAX_SIGNAL_GEOS}
              onChange={(e) => {
                const v = e.target.value;
                if (v && picked.length < MAX_SIGNAL_GEOS && !picked.includes(v)) {
                  setPicked([...picked, v]);
                }
              }}
              className={`mt-1 w-full px-2 py-1.5 rounded border text-xs ${isLightBundle(t) ? 'bg-white border-stone-300 text-stone-900' : 'bg-stone-900 border-stone-800 text-stone-100'}`}
            >
              <option value="">
                {picked.length >= MAX_SIGNAL_GEOS
                  ? `max ${MAX_SIGNAL_GEOS} per run`
                  : `add a ${geoMode === 'metros' ? 'metro' : 'state'}… (up to ${MAX_SIGNAL_GEOS})`}
              </option>
              {geoOptions.filter(([code]) => !picked.includes(code)).map(([code, name]) => (
                <option key={code} value={code}>{name}</option>
              ))}
            </select>
            {picked.length > 0 && (
              <div className="mt-1 flex flex-wrap gap-1">
                {picked.map((code) => (
                  <button
                    key={code}
                    onClick={() => setPicked(picked.filter((c) => c !== code))}
                    title="remove"
                    className={`px-1.5 py-0.5 rounded border text-[10px] ${isLightBundle(t) ? 'bg-stone-100 border-stone-300 text-stone-700 hover:bg-stone-200' : 'bg-stone-800 border-stone-700 text-stone-300 hover:bg-stone-700'}`}
                  >
                    {GEO_LABELS[code] || code} ×
                  </button>
                ))}
              </div>
            )}
          </>
        )}
      </div>
      <button
        onClick={run}
        disabled={running || needsPick}
        className="w-full mt-2 inline-flex items-center justify-center gap-2 rounded bg-vita-accent px-3 py-2 text-vita-navy text-xs font-medium hover:bg-vita-bright disabled:opacity-50 transition"
      >
        {running ? <Loader2 className="w-3 h-3 animate-spin" /> : <Radio className="w-3 h-3" />}
        {running ? 'Collecting…' : needsPick ? `Select a ${geoMode === 'metros' ? 'metro' : 'state'} first` : 'Collect Signals'}
      </button>
      {err && <div className="mt-2 text-[10px] text-red-500">{err}</div>}
      {lastResult && (
        <div className={`mt-2 text-[10px] ${t.mutedText} space-y-0.5`}>
          <div>status: <span className={lastResult.status === 'failed' ? 'text-red-500' : 'text-vita-accent'}>{lastResult.status}</span></div>
          {summary && (
            <>
              <div>collected: {summary.collected} (rt:{summary.collectStats?.trends_realtime} rel:{summary.collectStats?.trends_related} d:{summary.collectStats?.trends_daily})</div>
              {summary.geos && <div>geo: {summary.geos.map((g) => GEO_LABELS[g] || g).join(', ')}</div>}
              <div>ranked: {summary.ranked} · backfilled: {summary.embeddings_backfilled}</div>
              <div>
                feed: <span className="text-vita-accent">{summary.feed?.generated} keyword(s) → {(summary.feed?.generated || 0) * 2} segments</span>
                {' · '}{summary.feed?.matched} matched / {summary.feed?.failed} failed
              </div>
            </>
          )}
        </div>
      )}
      {lastResult?.status === 'completed' && (
        <div className={`mt-2 text-[10px] ${t.veryMutedText}`}>Run loaded into the dashboard below ↓</div>
      )}
      {/* Secondary: top up the news library the matcher draws from. Cron
          normally handles this; manual override lives here, not as a rival
          start button. */}
      <div className={`mt-3 pt-2 border-t ${t.divider}`}>
        <button
          onClick={refreshLibrary}
          disabled={refreshing}
          className={`w-full inline-flex items-center justify-center gap-2 rounded border px-3 py-1.5 text-[11px] ${t.btn} disabled:opacity-50 transition`}
        >
          {refreshing ? <Loader2 className="w-3 h-3 animate-spin" /> : <RefreshCw className="w-3 h-3" />}
          {refreshing ? 'Refreshing news library…' : 'Refresh news library'}
        </button>
        {refreshMsg && <div className={`mt-1 text-[10px] ${t.veryMutedText}`}>{refreshMsg}</div>}
      </div>
    </PanelCard>
  );
}
